import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
// Import API config to set axios defaults
import { fetchWithBaseURL, API_ENDPOINTS } from '../config/api';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Box, 
  Tabs, 
  Tab, 
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Grid,
  Card,
  CardContent,
  Chip,
  Divider,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Snackbar,
  Fab,
  useTheme,
  Link,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress
} from '@mui/material';
import { 
  Share as ShareIcon,
  WhatsApp as WhatsAppIcon,
  Twitter as TwitterIcon,
  Facebook as FacebookIcon,
  Telegram as TelegramIcon,
  ContentCopy as CopyIcon,
  Download as DownloadIcon,
  Close as CloseIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  EmojiEvents as TrophyIcon,
  Person as PersonIcon,
  Group as GroupIcon,
  ExpandMore as ExpandMoreIcon,
  CalendarToday as CalendarIcon,
  AccessTime as TimeIcon,
  LocationOn as LocationIcon,
  Payments as FeeIcon,
  Sports as SportsIcon,
  HowToReg as RegisterIcon,
  CheckCircle as CheckIcon,
  Groups as TeamIcon,
  ArrowUpward as ArrowUpwardIcon
} from '@mui/icons-material';
import { Helmet } from 'react-helmet-async';
import { renderAddressWithMapLink } from '../utils/addressUtils';
import { getAuth, onAuthStateChanged, User } from 'firebase/auth';
import { isAdminOrSuperAdmin, isSuperAdminEmail } from '../config/adminEmails';
import { Sponsor } from '../types';
import KnockoutBracket from './KnockoutBracket';

// Player Statistics Interface
interface PlayerStats {
  nama: string;
  rank: string;
  poin: number;
  winRate: number;
  total: number;
  wins: number;
  losses: number;
  previousPoints: string;
  winLossHistory: string;
  opponents: string;
  level: string;
  highestRank?: string; // Rank tertinggi yang pernah dicapai
  highestPoints?: number; // Poin tertinggi yang pernah dicapai
  overallRank?: number;
  levelRank?: number;
  levelTurnamen?: string; // Level Turnamen dari kolom P
}

interface InternalTournamentConfig {
  id: number;
  name: string;
  location: string;
  address: string;
  date: string;
  time: string;
  description: string;
  slot: string;
  fee: string;
}


// utility to shorten tournament name in dropdown
const shortenTournamentName = (name: string) => {
  if (!name) return '';
  if (name.length <= 30) return name;
  return name.slice(0, 27) + '...';
};

// Player Statistics Modal Component
const PlayerStatsModal: React.FC<{
  open: boolean;
  onClose: () => void;
  player: PlayerStats | null;
  category: string;
}> = ({ open, onClose, player, category }) => {
  if (!player) return null;

  const getCategoryIcon = () => {
    switch (category) {
      case 'ganda_solo':
        return <GroupIcon />;
      case 'ganda_party':
        return <GroupIcon />;
      default:
        return <PersonIcon />;
    }
  };

  const getCategoryName = () => {
    switch (category) {
      case 'ganda_solo':
        return 'Ganda (Solo)';
      case 'ganda_party':
        return 'Ganda (Party)';
      default:
        return 'Tunggal';
    }
  };

  // Fungsi untuk masking nama berdasarkan win rate
  const getMaskedName = (nama: string, winRate: number) => {
    if (winRate >= 50) {
      return nama; // Tampilkan nama lengkap jika win rate >= 50%
    }
    
    // Masking untuk win rate < 50%
    if (nama.length <= 2) {
      return '***'; // Jika nama terlalu pendek, mask semua
    }
    
    // Tampilkan huruf pertama + asterisk
    return nama.charAt(0) + '***';
  };

  const parseWinLossHistory = (history: string) => {
    if (!history) return [];
    return history.split(',').map((result, index) => ({
      match: index + 1,
      result: result.trim().toUpperCase(),
      isWin: result.trim().toUpperCase() === 'W'
    }));
  };

  const parseOpponents = (opponents: string) => {
    if (!opponents) return [];
    return opponents.split(',').map((opponent, index) => ({
      match: index + 1,
      opponent: opponent.trim()
    }));
  };

  const parsePreviousPoints = (previousPoints: string) => {
    if (!previousPoints) return [];
    return previousPoints.split(',').map((point, index) => ({
      match: index + 1,
      previousPoint: parseInt(point.trim()) || 0
    }));
  };

  const winLossData = parseWinLossHistory(player.winLossHistory);
  const opponentsData = parseOpponents(player.opponents);
  const previousPointsData = parsePreviousPoints(player.previousPoints);

  // Reverse data untuk menampilkan dari yang terbaru ke terlama
  const reversedWinLossData = [...winLossData].reverse();
  const reversedOpponentsData = [...opponentsData].reverse();
  const reversedPreviousPointsData = [...previousPointsData].reverse();

  const capturePlayerStatsScreenshot = async (): Promise<string> => {
    try {
      const html2canvas = (await import('html2canvas')).default;
      
      // Capture the entire modal content
      const modalElement = document.querySelector('[role="dialog"]') as HTMLElement;
      if (!modalElement) return '';
      
      const canvas = await html2canvas(modalElement, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
        allowTaint: true,
        scrollX: 0,
        scrollY: 0,
        width: modalElement.scrollWidth,
        height: modalElement.scrollHeight
      });
      
      return canvas.toDataURL('image/png');
    } catch (error) {
      console.error('Error capturing player stats screenshot:', error);
      return '';
    }
  };

  const createShareableImage = async (screenshot: string, text: string): Promise<string> => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        resolve('');
        return;
      }

      // Set canvas size
      canvas.width = 800;
      canvas.height = 1000;

      // Create background
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Add screenshot if available
      if (screenshot) {
        const img = new Image();
        img.onload = () => {
          // Resize and center the screenshot
          const maxWidth = 700;
          const maxHeight = 600;
          const ratio = Math.min(maxWidth / img.width, maxHeight / img.height);
          const newWidth = img.width * ratio;
          const newHeight = img.height * ratio;
          
          const x = (canvas.width - newWidth) / 2;
          const y = 50;
          
          ctx.drawImage(img, x, y, newWidth, newHeight);
          
          // Add text below screenshot
          ctx.fillStyle = '#333333';
          ctx.font = 'bold 24px Arial';
          ctx.textAlign = 'center';
          ctx.fillText('🏆 Statistik Pemain RaketerS', canvas.width / 2, y + newHeight + 80);
          
          ctx.font = '18px Arial';
          const lines = text.split('\n');
          let textY = y + newHeight + 120;
          
          lines.forEach(line => {
            if (line.trim()) {
              ctx.fillText(line, canvas.width / 2, textY);
              textY += 30;
            }
          });
          
          // Add website URL
          ctx.fillStyle = '#25D366';
          ctx.font = 'bold 16px Arial';
          ctx.fillText('🔗 raketers.com', canvas.width / 2, textY + 40);
          
          resolve(canvas.toDataURL('image/png'));
        };
        img.src = screenshot;
      } else {
        // If no screenshot, just add text
        ctx.fillStyle = '#333333';
        ctx.font = 'bold 24px Arial';
        ctx.textAlign = 'center';
        ctx.fillText('🏆 Statistik Pemain RaketerS', canvas.width / 2, 100);
        
        ctx.font = '18px Arial';
        const lines = text.split('\n');
        let textY = 150;
        
        lines.forEach(line => {
          if (line.trim()) {
            ctx.fillText(line, canvas.width / 2, textY);
            textY += 30;
          }
        });
        
        // Add website URL
        ctx.fillStyle = '#25D366';
        ctx.font = 'bold 16px Arial';
        ctx.fillText('🔗 raketers.com', canvas.width / 2, textY + 40);
        
        resolve(canvas.toDataURL('image/png'));
      }
    });
  };

  const sharePlayerStats = async () => {
    const statsText = `🏆 Statistik ${player.nama} - ${getCategoryName()}
    
📊 Ranking: ${player.rank} (${player.poin} poin)
🎯 Win Rate: ${player.winRate}%
📈 Total Match: ${player.total}
✅ Menang: ${player.wins}
❌ Kalah: ${player.losses}

#BookingMabar #RankedMatch #${getCategoryName()}`;

    try {
      // Capture screenshot
      const screenshot = await capturePlayerStatsScreenshot();
      
      // Create shareable image
      const shareableImage = await createShareableImage(screenshot, statsText);
      
      if (shareableImage) {
        // Download the image
        const link = document.createElement('a');
        link.download = `statistik-${player.nama.replace(/\s+/g, '-').toLowerCase()}.png`;
        link.href = shareableImage;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Open WhatsApp with text
        const whatsappText = `${statsText}\n\n🔗 ${window.location.href}`;
        const url = `https://wa.me/?text=${encodeURIComponent(whatsappText)}`;
        window.open(url, '_blank');
        
        // Show success message
        alert('Gambar telah didownload! Silakan upload gambar tersebut ke WhatsApp bersama dengan teks yang sudah disiapkan.');
      } else {
        // Fallback to text only
        if (navigator.share) {
          navigator.share({
            title: `Statistik ${player.nama}`,
            text: statsText,
            url: window.location.href
          });
        } else {
          navigator.clipboard.writeText(statsText);
          alert('Teks statistik telah disalin ke clipboard!');
        }
      }
    } catch (error) {
      console.error('Error sharing player stats:', error);
      // Fallback to text only
      if (navigator.share) {
        navigator.share({
          title: `Statistik ${player.nama}`,
          text: statsText,
          url: window.location.href
        });
      } else {
        navigator.clipboard.writeText(statsText);
        alert('Teks statistik telah disalin ke clipboard!');
      }
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          background: (theme) => theme.palette.mode === 'dark' 
            ? 'linear-gradient(135deg, rgba(0,0,0,0.9) 0%, rgba(20,20,20,0.95) 100%)'
            : 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,248,248,0.98) 100%)',
          border: (theme) => theme.palette.mode === 'dark' 
            ? '1px solid rgba(255,255,255,0.1)'
            : '1px solid rgba(0,0,0,0.1)',
          borderRadius: 3
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 2,
        color: 'text.primary',
        borderBottom: (theme) => theme.palette.mode === 'dark' 
          ? '1px solid rgba(255,255,255,0.1)'
          : '1px solid rgba(0,0,0,0.1)'
      }}>
        {getCategoryIcon()}
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {getMaskedName(player.nama, player.winRate)}
          </Typography>
          <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
            {getCategoryName()}
          </Typography>
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        <IconButton onClick={onClose} sx={{ color: 'text.primary' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Grid container spacing={3}>
          {/* Basic Stats */}
          <Grid item xs={12} md={5}>
            <Card sx={{ 
              background: (theme) => theme.palette.mode === 'dark' 
                ? 'linear-gradient(135deg, rgba(13,71,161,0.08) 0%, rgba(21,101,192,0.04) 100%)'
                : 'rgba(0,0,0,0.02)',
              border: (theme) => theme.palette.mode === 'dark' 
                ? '1px solid rgba(99,170,255,0.22)'
                : '1px solid rgba(0,0,0,0.1)',
              height: 'fit-content',
              borderRadius: 3,
              '&:hover': {
                boxShadow: (theme) => theme.palette.mode === 'dark' 
                  ? '0 12px 40px rgba(99,170,255,0.35)'
                  : '0 8px 24px rgba(0,0,0,0.08)',
                borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(99,170,255,0.35)' : undefined,
                transition: 'all 0.3s ease'
              }
            }}>
              <CardContent>
                <Typography variant="h6" sx={{ 
                  color: 'text.primary', 
                  mb: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}>
                  <TrophyIcon />
                  Statistik Dasar
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Ranking:
                    </Typography>
                    <Chip 
                      label={player.rank} 
                      sx={{ 
                        background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                        color: 'white',
                        fontWeight: 600
                      }} 
                    />
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Poin:
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'text.primary', fontWeight: 600 }}>
                      {player.poin}
                    </Typography>
                  </Box>

                  {player.overallRank && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Rank All Level:
                      </Typography>
                      <Chip
                        label={`#${player.overallRank}`}
                        size="small"
                        sx={{
                          background: 'linear-gradient(45deg, #FF6B35 30%, #F7931E 90%)',
                          color: 'white',
                          fontWeight: 600
                        }}
                      />
                    </Box>
                  )}

                  {player.levelRank && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Rank pada Level {player.level}:
                      </Typography>
                      <Chip
                        label={`#${player.levelRank}`}
                        size="small"
                        sx={{
                          background: 'linear-gradient(45deg, #9C27B0 30%, #BA68C8 90%)',
                          color: 'white',
                          fontWeight: 600
                        }}
                      />
                    </Box>
                  )}

                  {player.highestRank && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Rank Tertinggi:
                      </Typography>
                      <Chip 
                        label={player.highestRank} 
                        size="small"
                        sx={{ 
                          background: 'linear-gradient(45deg, #FF6B35 30%, #F7931E 90%)',
                          color: 'white',
                          fontWeight: 600
                        }} 
                      />
                    </Box>
                  )}
                  
                  {player.highestPoints && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Poin Tertinggi:
                      </Typography>
                      <Typography variant="body1" sx={{ 
                        color: 'primary.main', 
                        fontWeight: 700,
                        fontSize: '1.1rem'
                      }}>
                        {player.highestPoints}
                      </Typography>
                    </Box>
                  )}
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Win Rate:
                    </Typography>
                    <Typography variant="body1" sx={{ 
                      color: player.winRate >= 50 ? '#4CAF50' : '#F44336',
                      fontWeight: 600
                    }}>
                      {player.winRate}%
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Total Match:
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'text.primary', fontWeight: 600 }}>
                      {player.total}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Menang:
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#4CAF50', fontWeight: 600 }}>
                      {player.wins}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Kalah:
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#F44336', fontWeight: 600 }}>
                      {player.losses}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Combined Win/Loss History with Opponents */}
          <Grid item xs={12} md={7}>
            <Card sx={{ 
              background: (theme) => theme.palette.mode === 'dark' 
                ? 'linear-gradient(135deg, rgba(13,71,161,0.08) 0%, rgba(21,101,192,0.04) 100%)'
                : 'rgba(0,0,0,0.02)',
              border: (theme) => theme.palette.mode === 'dark' 
                ? '1px solid rgba(99,170,255,0.22)'
                : '1px solid rgba(0,0,0,0.1)',
              height: 'fit-content',
              borderRadius: 3,
              '&:hover': {
                boxShadow: (theme) => theme.palette.mode === 'dark' 
                  ? '0 12px 40px rgba(99,170,255,0.35)'
                  : '0 8px 24px rgba(0,0,0,0.08)',
                borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(99,170,255,0.35)' : undefined,
                transition: 'all 0.3s ease'
              }
            }}>
              <CardContent>
                <Typography variant="h6" sx={{ 
                  color: 'text.primary', 
                  mb: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}>
                  <TrendingUpIcon />
                  10 Pertandingan Terakhir
                </Typography>
                
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
                  {reversedWinLossData.map((match, index) => {
                    const opponent = reversedOpponentsData[index]?.opponent || 'Unknown';
                    const isWin = match.isWin;
                    
                    // Logika perhitungan poin yang benar:
                    // previousPoints berisi history poin dari pertandingan paling lama ke paling baru
                    // Data sudah di-reverse, jadi index 0 = pertandingan terbaru
                    // Untuk pertandingan terbaru (index 0): previousPoints[0] → poin terkini
                    // Untuk pertandingan ke-2 (index 1): previousPoints[1] → previousPoints[0]
                    // dst...
                    const previousPoint = reversedPreviousPointsData[index]?.previousPoint || 0;
                    const currentPoint = index === 0 
                      ? player.poin 
                      : reversedPreviousPointsData[index - 1]?.previousPoint || player.poin;
                    const pointChange = currentPoint - previousPoint;
                    const isPointIncrease = pointChange > 0;
                    
                    return (
                      <Box
                        key={index}
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: 0.5
                        }}
                      >
                        <Chip
                          label={opponent}
                          sx={{
                            background: isWin 
                              ? 'linear-gradient(45deg, #4CAF50 30%, #66BB6A 90%)' 
                              : 'linear-gradient(45deg, #F44336 30%, #EF5350 90%)',
                            color: 'white',
                            fontWeight: 500,
                            minHeight: '36px',
                            fontSize: '0.9rem',
                            '& .MuiChip-label': {
                              px: 2,
                              py: 1
                            },
                            boxShadow: isWin 
                              ? '0 2px 4px rgba(76, 175, 80, 0.3)' 
                              : '0 2px 4px rgba(244, 67, 54, 0.3)',
                            '&:hover': {
                              transform: 'translateY(-1px)',
                              boxShadow: isWin 
                                ? '0 4px 8px rgba(76, 175, 80, 0.4)' 
                                : '0 4px 8px rgba(244, 67, 54, 0.4)'
                            },
                            transition: 'all 0.2s ease-in-out'
                          }}
                        />
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 0.5,
                          minWidth: 'fit-content'
                        }}>
                          <Typography variant="caption" sx={{ 
                            color: 'text.secondary',
                            fontSize: '0.7rem',
                            fontWeight: 500
                          }}>
                            {previousPoint}
                          </Typography>
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            color: isPointIncrease ? '#4CAF50' : '#F44336'
                          }}>
                            {isPointIncrease ? (
                              <TrendingUpIcon sx={{ fontSize: '0.8rem' }} />
                            ) : (
                              <TrendingDownIcon sx={{ fontSize: '0.8rem' }} />
                            )}
                          </Box>
                          <Typography variant="caption" sx={{ 
                            color: isPointIncrease ? '#4CAF50' : '#F44336',
                            fontSize: '0.7rem',
                            fontWeight: 600
                          }}>
                            {currentPoint}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                  {reversedWinLossData.length === 0 && (
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Belum ada data pertandingan
                    </Typography>
                  )}
                </Box>
                
                {/* Legend */}
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  gap: 1.5,
                  pt: 2, 
                  borderTop: '1px solid rgba(255,255,255,0.1)',
                  alignItems: 'center'
                }}>
                  <Box sx={{ 
                    display: 'flex', 
                    gap: 3,
                    justifyContent: 'center'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ 
                        width: 14, 
                        height: 14, 
                        borderRadius: '50%', 
                        background: '#4CAF50',
                        boxShadow: '0 2px 4px rgba(76, 175, 80, 0.3)'
                      }} />
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                        Menang
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ 
                        width: 14, 
                        height: 14, 
                        borderRadius: '50%', 
                        background: '#F44336',
                        boxShadow: '0 2px 4px rgba(244, 67, 54, 0.3)'
                      }} />
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                        Kalah
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ 
                    display: 'flex', 
                    gap: 3,
                    justifyContent: 'center'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TrendingUpIcon sx={{ fontSize: '1rem', color: '#4CAF50' }} />
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                        Poin Naik
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TrendingDownIcon sx={{ fontSize: '1rem', color: '#F44336' }} />
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                        Poin Turun
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 3, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
        <Button
          onClick={sharePlayerStats}
          startIcon={<ShareIcon />}
          variant="contained"
          sx={{
            background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
            color: 'white',
            fontWeight: 600,
            '&:hover': {
              background: 'linear-gradient(45deg, #1976D2 30%, #1CB5E0 90%)',
            }
          }}
        >
          Share Statistik
        </Button>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
            color: (theme) => theme.palette.mode === 'dark' ? 'white' : 'black',
            '&:hover': {
              borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)',
              background: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'
            }
          }}
        >
          Tutup
        </Button>
      </DialogActions>
    </Dialog>
  );
};

function TabPanel(props: any) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`leaderboard-tabpanel-${index}`}
      aria-labelledby={`leaderboard-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3, pt: 0 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// Data akan diambil dari API - tidak ada data dummy

// Data Ranked Match - akan diisi dari API

// Rank levels dengan warna dan deskripsi
const rankLevels = [
  { name: 'Shuttle', color: '#8BC34A', description: 'Pemula yang baru mulai', minPoints: 0, icon: '/ranks/shuttle.svg' },
  { name: 'Racketwood', color: '#4CAF50', description: 'Pemain yang sudah terbiasa', minPoints: 250, icon: '/ranks/racketwood.svg' },
  { name: 'Rally Machine', color: '#2196F3', description: 'Pemain yang konsisten', minPoints: 500, icon: '/ranks/rally-machine.svg' },
  { name: 'True Racketers', color: '#9C27B0', description: 'Pemain yang handal', minPoints: 750, icon: '/ranks/true-racketers.svg' },
  { name: 'Champion', color: '#FF9800', description: 'Pemain elite', minPoints: 900, icon: '/ranks/champion.svg' },
  { name: 'Legend', color: '#F44336', description: 'Pemain legendaris', minPoints: 1000, icon: '/ranks/legend.svg' }
];

// Komponen untuk menampilkan nomor urutan rank
const RankNumber: React.FC<{ position: number }> = ({ position }) => {
  if (position <= 3) {
    return (
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        width: { xs: 32, sm: 36, md: 40 },
        height: { xs: 32, sm: 36, md: 40 },
        borderRadius: '50%',
        position: 'relative',
        boxShadow: { xs: 2, sm: 3 },
        mx: 'auto'
      }}>
        {/* Background gradient untuk peringkat 1-3 */}
        {position === 1 && (
          <Box sx={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 50%, #FF8C00 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 8px rgba(255, 215, 0, 0.4)'
          }}>
            <Typography sx={{ 
              color: 'white', 
              fontWeight: 900, 
              fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' },
              textShadow: '0 2px 4px rgba(0,0,0,0.3)'
            }}>
              {position}
            </Typography>
            {/* Crown untuk peringkat 1 */}
            <Box sx={{
              position: 'absolute',
              top: -8,
              left: '50%',
              transform: 'translateX(-50%)',
              fontSize: '0.8rem'
            }}>
              👑
            </Box>
          </Box>
        )}
        
        {position === 2 && (
          <Box sx={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #C0C0C0 0%, #A8A8A8 50%, #808080 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 8px rgba(192, 192, 192, 0.4)'
          }}>
            <Typography sx={{ 
              color: 'white', 
              fontWeight: 900, 
              fontSize: '1.2rem',
              textShadow: '0 2px 4px rgba(0,0,0,0.3)'
            }}>
              {position}
            </Typography>
            {/* Wings untuk peringkat 2 */}
            <Box sx={{
              position: 'absolute',
              left: -6,
              top: '50%',
              transform: 'translateY(-50%)',
              fontSize: '0.6rem'
            }}>
              🪶
            </Box>
            <Box sx={{
              position: 'absolute',
              right: -6,
              top: '50%',
              transform: 'translateY(-50%)',
              fontSize: '0.6rem'
            }}>
              🪶
            </Box>
          </Box>
        )}
        
        {position === 3 && (
          <Box sx={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #CD7F32 0%, #B8860B 50%, #8B4513 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 8px rgba(205, 127, 50, 0.4)'
          }}>
            <Typography sx={{ 
              color: 'white', 
              fontWeight: 900, 
              fontSize: '1.2rem',
              textShadow: '0 2px 4px rgba(0,0,0,0.3)'
            }}>
              {position}
            </Typography>
            {/* Wings untuk peringkat 3 */}
            <Box sx={{
              position: 'absolute',
              left: -6,
              top: '50%',
              transform: 'translateY(-50%)',
              fontSize: '0.6rem'
            }}>
              🪶
            </Box>
            <Box sx={{
              position: 'absolute',
              right: -6,
              top: '50%',
              transform: 'translateY(-50%)',
              fontSize: '0.6rem'
            }}>
              🪶
            </Box>
          </Box>
        )}
      </Box>
    );
  }
  
  // Untuk peringkat 4 ke atas
  return (
    <Box sx={{ 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      width: { xs: 28, sm: 32, md: 36 },
      height: { xs: 28, sm: 32, md: 36 },
      borderRadius: '50%',
      background: 'rgba(110,120,130,0.3)',
      mx: 'auto'
    }}>
      <Typography sx={{ color: 'white', fontWeight: 700, fontSize: { xs: '0.85rem', sm: '0.99rem', md: '1.1rem' } }}>
        {position}
      </Typography>
    </Box>
  );
};

// Fungsi untuk mendapatkan warna level
const getLevelColor = (level: string) => {
  const levelLower = level?.toLowerCase() || '';
  if (levelLower.includes('beginner') || levelLower.includes('pemula')) {
    return '#4CAF50'; // Hijau
  } else if (levelLower.includes('intermediate') || levelLower.includes('menengah')) {
    return '#FF9800'; // Kuning/Orange
  } else if (levelLower.includes('advanced') || levelLower.includes('lanjutan') || levelLower.includes('mahir')) {
    return '#F44336'; // Merah
  } else if (levelLower.includes('expert') || levelLower.includes('ahli')) {
    return '#9C27B0'; // Ungu
  } else if (levelLower.includes('master')) {
    return '#2196F3'; // Biru
  } else {
    return '#757575'; // Abu-abu default
  }
};

// Komponen untuk menampilkan rank badge
const RankBadge: React.FC<{ rank: string }> = ({ rank }) => {
  const rankInfo = rankLevels.find(r => r.name === rank);
  return (
    <Box sx={{ 
      display: 'flex', 
      justifyContent: 'center', // Center horizontal
      alignItems: 'center',
      width: '100%',
      textAlign: 'center'
    }}>
      <img 
        src={rankInfo?.icon || '/ranks/shuttle.svg'} 
        alt={rank} 
        style={{ 
          width: window.innerWidth < 600 ? 22 : 40, 
          height: window.innerWidth < 600 ? 22 : 40,
          flexShrink: 0,
          margin: '0 auto',
          display: 'block',
          textAlign: 'center'
        }}
      />
      <span style={{ fontWeight: 700, color: rankInfo?.color || '#fff', marginLeft: 4, textAlign: 'center', width: '100%' }}>{rank}</span>
    </Box>
  );
};

interface LeaderboardProps {
  initialMainTab?: number;
  showOnlyRanked?: boolean;
  showOnlyTournament?: boolean;
  showOnlyLiga?: boolean;
}

const Leaderboard: React.FC<LeaderboardProps> = ({
  initialMainTab = 1,
  showOnlyRanked = false,
  showOnlyTournament = false,
  showOnlyLiga = false,
}) => {
  const navigate = useNavigate();
  const { playerName } = useParams<{ playerName?: string }>();
  const theme = useTheme();
  const mode = theme.palette.mode;
  const [mainTab, setMainTab] = useState(initialMainTab); // Start with Ranked Match tab or provided tab
  const [sponsors, setSponsors] = useState<Sponsor[]>([]);
  
  // State untuk form ranked match
  const [rankedMatchData, setRankedMatchData] = useState({
    ranked_single: [],
    ranked_double_solo: [],
    ranked_double_party: []
  });
  const [rankedMatchLoading, setRankedMatchLoading] = useState(false);
  const [rankedSeason1Top3ByLevel, setRankedSeason1Top3ByLevel] = useState<{
    gabungan: any[];
    beginner: any[];
    intermediate: any[];
    advanced: any[];
  }>({ gabungan: [], beginner: [], intermediate: [], advanced: [] });
  const [rankedSeason2Top3ByLevel, setRankedSeason2Top3ByLevel] = useState<{
    gabungan: any[];
    beginner: any[];
    intermediate: any[];
    advanced: any[];
  }>({ gabungan: [], beginner: [], intermediate: [], advanced: [] });
  const [selectedSeasonChampion, setSelectedSeasonChampion] = useState<'season1' | 'season2'>('season2');
  const [rankedSeason1Loading, setRankedSeason1Loading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('ganda_solo');
  const [selectedLevel, setSelectedLevel] = useState('Gabungan');
  
  // Liga state
  const [ligaLocation, setLigaLocation] = useState<string>('Liga Corona');
  const [ligaData, setLigaData] = useState<Array<{ rank: string | number; nama: string; poin: number; jumlahMain: number; menang: number; kalah: number; seri: number; last10: string }>>([]);
  const [ligaLoading, setLigaLoading] = useState<boolean>(false);
  const [ligaError, setLigaError] = useState<string | null>(null);

  // Tournament standings & matches state
  const [tournamentStandings, setTournamentStandings] = useState<any[]>([]);
  const [tournamentMatches, setTournamentMatches] = useState<any[]>([]);
  const [tournamentKnockout, setTournamentKnockout] = useState<any[]>([]);
  const [tournamentLoading, setTournamentLoading] = useState<boolean>(false);
  const [tournamentError, setTournamentError] = useState<string | null>(null);
  const [tournamentTab, setTournamentTab] = useState(0); // 0 = Klasemen, 1 = Babak Gugur

  // Internal tournament config & form state
  const [internalTournaments, setInternalTournaments] = useState<InternalTournamentConfig[]>([]);
  const [selectedInternalTournamentId, setSelectedInternalTournamentId] = useState<number | null>(null);
  const [internalForms, setInternalForms] = useState<Record<number, {
    namaP1: string;
    levelP1: string;
    email: string;
    nomorHP: string;
  }>>({});
  const [internalStatuses, setInternalStatuses] = useState<Record<number, {
    submitting: boolean;
    error: string | null;
    success: string | null;
  }>>({});
  const [registrationDialogOpen, setRegistrationDialogOpen] = useState(false);

  // Payment state for internal tournament registrations
  const [paymentOrderId, setPaymentOrderId] = useState<string | null>(null);
  const [paymentRetryCount, setPaymentRetryCount] = useState(0);
  const [showPaymentErrorDialog, setShowPaymentErrorDialog] = useState(false);

  // Registrations that have been submitted (filtered by current selected tournament)
  const [internalRegistrations, setInternalRegistrations] = useState<Array<any>>([]);
  
  // Share functionality state
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const tableRef = useRef<HTMLDivElement>(null);
  const cardViewRef = useRef<HTMLDivElement>(null);
  
  // Mobile view state - default card view untuk mobile
  const [isMobileView, setIsMobileView] = useState(true);
  
  // Admin state
  const [user, setUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [adminEmail, setAdminEmail] = useState('');
  const [resetPassword, setResetPassword] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [resetting, setResetting] = useState(false);
  const [resetSeasonDialogOpen, setResetSeasonDialogOpen] = useState(false);

  // State untuk Accordion expand/collapse - semua default collapsed
  const [expandedAccordion, setExpandedAccordion] = useState<string | false>(false);

  const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedAccordion(isExpanded ? panel : false);
  };


  // Sinkronkan tab utama dengan props ketika mode tampilan / route berubah
  useEffect(() => {
    if (showOnlyRanked) {
      setMainTab(1);
    } else if (showOnlyTournament) {
      setMainTab(0);
    } else if (showOnlyLiga) {
      setMainTab(2);
    } else {
      setMainTab(initialMainTab);
    }
  }, [showOnlyRanked, showOnlyTournament, showOnlyLiga, initialMainTab]);

  const pageHeading = showOnlyTournament
    ? 'Turnamen Internal RaketerS'
    : showOnlyLiga
      ? 'Liga RaketerS'
      : 'GEARPADEL X BWS Ranked Match By RaketerS';

  // Player stats modal state
  const [playerStatsModalOpen, setPlayerStatsModalOpen] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState<PlayerStats | null>(null);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');

  // registration fetching logic moved below where selectedInternalTournament is declared

  // Trigger pembayaran Midtrans untuk internal tournament
  const startMidtransPayment = async (overrideOrderId?: string, isRetry: boolean = false) => {
    try {
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      const baseAmount = selectedInternalTournament && selectedInternalTournament.fee
        ? parseInt(selectedInternalTournament.fee.replace(/[^0-9]/g, ''), 10)
        : 0;

      if (!currentUser?.email) {
        alert('Silakan login terlebih dahulu untuk melakukan pembayaran');
        return;
      }

      const orderId = overrideOrderId || paymentOrderId || `ITR-${Date.now()}`;
      setPaymentOrderId(orderId);

      const response = await axios.post('/api/payment/snap-token', {
        order_id: orderId,
        gross_amount: baseAmount,
        nama: currentUser.displayName || 'Pasangan',
        email: currentUser.email,
      });

      const redirectUrl = response.data?.redirect_url;
      if (redirectUrl) {
        setPaymentRetryCount(0);
        window.location.href = redirectUrl;
      } else {
        handlePaymentFailure();
      }
    } catch (err) {
      console.error('Gagal memulai pembayaran Midtrans (internal tournament):', err);
      handlePaymentFailure();
    }
  };

  const handlePaymentFailure = () => {
    const newRetryCount = paymentRetryCount + 1;
    setPaymentRetryCount(newRetryCount);
    setPaymentOrderId(null);
    if (newRetryCount < 2) {
      setTimeout(() => {
        startMidtransPayment(undefined, true);
      }, 2000);
    } else {
      setShowPaymentErrorDialog(true);
    }
  };

  const handleRetryPayment = () => {
    setShowPaymentErrorDialog(false);
    setPaymentRetryCount(0);
    startMidtransPayment();
  };

  // Helper warna rank
  const getRankColor = (rankName: string) => {
    const match = rankLevels.find(r => r.name.toLowerCase() === (rankName || '').toLowerCase());
    return match?.color || '#1a73e8';
  };

  // Fetch ranked match leaderboard data
  useEffect(() => {
    const fetchRankedMatchData = async () => {
      setRankedMatchLoading(true);
      setRankedSeason1Loading(true);
      try {
        const [currentRes, season1Res, season2Res] = await Promise.all([
          axios.get('/api/bookings?action=ranked-leaderboard'),
          axios.get('/api/bookings?action=ranked-leaderboard&season=1'),
          axios.get('/api/bookings?action=ranked-leaderboard&season=2')
        ]);

        if (currentRes.status === 200) {
          const data = currentRes.data;
          console.log('Ranked match data received:', data);
          console.log('Ranked single data:', data.ranked_single);
          setRankedMatchData(data);
        }

        const normalizeCanonical = (lvl: string) => {
          const lower = (lvl || '').toLowerCase();
          if (lower.includes('beginner') || lower.includes('pemula')) return 'beginner';
          if (lower.includes('intermediate') || lower.includes('menengah')) return 'intermediate';
          if (lower.includes('advanced') || lower.includes('lanjutan') || lower.includes('mahir')) return 'advanced';
          return null;
        };

        const pickTop3 = (data: any[]) =>
          [...data].sort((a: any, b: any) => (b.poin || 0) - (a.poin || 0)).slice(0, 3);

        if (season1Res.status === 200) {
          const seasonData = season1Res.data;
          const seasonDoubleSolo = Array.isArray(seasonData?.ranked_double_solo) ? seasonData.ranked_double_solo : [];

          const grouped = {
            gabungan: pickTop3(seasonDoubleSolo),
            beginner: pickTop3(seasonDoubleSolo.filter((p: any) => normalizeCanonical(p.level || '') === 'beginner')),
            intermediate: pickTop3(seasonDoubleSolo.filter((p: any) => normalizeCanonical(p.level || '') === 'intermediate')),
            advanced: pickTop3(seasonDoubleSolo.filter((p: any) => normalizeCanonical(p.level || '') === 'advanced')),
          };

          setRankedSeason1Top3ByLevel(grouped);
        } else {
          setRankedSeason1Top3ByLevel({ gabungan: [], beginner: [], intermediate: [], advanced: [] });
        }

        if (season2Res.status === 200) {
          const seasonData = season2Res.data;
          const seasonDoubleSolo = Array.isArray(seasonData?.ranked_double_solo) ? seasonData.ranked_double_solo : [];

          const grouped = {
            gabungan: pickTop3(seasonDoubleSolo),
            beginner: pickTop3(seasonDoubleSolo.filter((p: any) => normalizeCanonical(p.level || '') === 'beginner')),
            intermediate: pickTop3(seasonDoubleSolo.filter((p: any) => normalizeCanonical(p.level || '') === 'intermediate')),
            advanced: pickTop3(seasonDoubleSolo.filter((p: any) => normalizeCanonical(p.level || '') === 'advanced')),
          };

          setRankedSeason2Top3ByLevel(grouped);
        } else {
          setRankedSeason2Top3ByLevel({ gabungan: [], beginner: [], intermediate: [], advanced: [] });
        }
      } catch (error) {
        console.error('Error fetching ranked match data:', error);
      } finally {
        setRankedMatchLoading(false);
        setRankedSeason1Loading(false);
      }
    };

    fetchRankedMatchData();
  }, []);

  // Fetch sponsors
  useEffect(() => {
    const fetchSponsors = async () => {
      try {
        const response = await axios.get<Sponsor[]>('/api/bookings?action=sponsor');
        setSponsors(response.data);
      } catch (error) {
        console.error('Error fetching sponsors:', error);
        setSponsors([]);
      }
    };

    fetchSponsors();
  }, []);

  // Fetch internal tournament config (khusus halaman /internal-tournament)
  useEffect(() => {
    const parseTournamentDate = (dateStr: string): number => {
      if (!dateStr || !dateStr.trim()) return Number.MAX_SAFE_INTEGER;
      const bulanMap: Record<string, number> = {
        'Januari': 0, 'Februari': 1, 'Maret': 2, 'April': 3, 'Mei': 4, 'Juni': 5,
        'Juli': 6, 'Agustus': 7, 'September': 8, 'Oktober': 9, 'November': 10, 'Desember': 11
      };
      const parts = dateStr.trim().split(/\s+/);
      if (parts.length < 3) return Number.MAX_SAFE_INTEGER;
      const d = parseInt(parts[0], 10);
      const bulan = bulanMap[parts[1]] ?? 0;
      const y = parseInt(parts[2], 10);
      return new Date(y, bulan, d).getTime();
    };

    const fetchInternalTournamentConfig = async () => {
      if (!showOnlyTournament) return;
      try {
        const response = await axios.get<InternalTournamentConfig[]>(API_ENDPOINTS.INTERNAL_TOURNAMENT_CONFIG);
        const raw = Array.isArray(response.data) ? response.data : [];
        const sorted = [...raw].sort((a, b) => parseTournamentDate(a.date) - parseTournamentDate(b.date));
        setInternalTournaments(sorted);
        if (sorted.length > 0) {
          setSelectedInternalTournamentId(prev => prev !== null ? prev : sorted[0].id);
        }
      } catch (err) {
        console.error('Error fetching internal tournament config:', err);
        setInternalTournaments([]);
      }
    };

    fetchInternalTournamentConfig();
  }, [showOnlyTournament]);

  // Fetch tournament standings and matches data
  useEffect(() => {
    const fetchTournamentData = async () => {
      if (!showOnlyTournament) return;
      
      setTournamentLoading(true);
      setTournamentError(null);
      try {
        // Fetch standings data
        const standingsResponse = await fetch('/api/bookings?action=tournament-standings');
        if (standingsResponse.ok) {
          const standingsResult = await standingsResponse.json();
          setTournamentStandings(standingsResult.data || []);
        } else {
          setTournamentStandings([]);
        }

        // Fetch matches data
        const matchesResponse = await fetch('/api/bookings?action=tournament-matches');
        if (matchesResponse.ok) {
          const matchesResult = await matchesResponse.json();
          setTournamentMatches(matchesResult.data || []);
        } else {
          setTournamentMatches([]);
        }

        // Fetch knockout data
        const knockoutResponse = await fetch('/api/bookings?action=tournament-knockout');
        if (knockoutResponse.ok) {
          const knockoutResult = await knockoutResponse.json();
          setTournamentKnockout(knockoutResult.data || []);
        } else {
          setTournamentKnockout([]);
        }
      } catch (err) {
        setTournamentError('Gagal mengambil data turnamen');
        setTournamentStandings([]);
        setTournamentMatches([]);
      } finally {
        setTournamentLoading(false);
      }
    };

    fetchTournamentData();
  }, [showOnlyTournament]);

  // Check admin status
  // isAdminOrSuperAdmin: untuk fitur admin umum (status indicator, dll)
  // isSuperAdmin: untuk fitur yang hanya bisa diakses superadmin (update ranked match)
  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      if (user && user.email) {
        const isAdminOrSuper = isAdminOrSuperAdmin(user.email);
        const isSuper = isSuperAdminEmail(user.email);
        setIsAdmin(isAdminOrSuper);
        setIsSuperAdmin(isSuper);
        setAdminEmail(user.email);
      } else {
        setIsAdmin(false);
        setIsSuperAdmin(false);
        setAdminEmail('');
      }
    });

    return () => unsubscribe();
  }, []);

  // Prefill email form turnamen internal dari user yang login (jika ada)
  useEffect(() => {
    if (showOnlyTournament && user && user.email) {
      setInternalForms(prev => {
        const next = { ...prev };
        internalTournaments.forEach(t => {
          const current = next[t.id] || {
            namaP1: '',
            levelP1: '',
            email: '',
            nomorHP: '',
          };
          next[t.id] = {
            ...current,
            email: current.email || user.email || '',
          };
        });
        return next;
      });
    }
  }, [showOnlyTournament, user, internalTournaments]);

  // Turnamen internal yang sedang dipilih (berdasarkan dropdown)
  const selectedInternalTournament =
    showOnlyTournament && internalTournaments.length > 0
      ? internalTournaments.find((t) => t.id === selectedInternalTournamentId) ||
        internalTournaments[0]
      : null;

  // compute helpers for rendering form/table
  const activeInternalTournament = selectedInternalTournament;
  const levelOptions = [
    'Beginner',
    'Intermediate -',
    'Intermediate',
    'Intermediate +',
    'Advanced -',
    'Advanced',
  ];
  const activeForm = activeInternalTournament
    ? internalForms[activeInternalTournament.id] || {
        namaP1: '',
        levelP1: '',
        email: '',
        nomorHP: '',
      }
    : {
        namaP1: '',
        levelP1: '',
        email: '',
        nomorHP: '',
      };
  const activeStatus = activeInternalTournament
    ? internalStatuses[activeInternalTournament.id] || {
        submitting: false,
        error: null,
        success: null,
      }
    : {
        submitting: false,
        error: null,
        success: null,
      };

  // load registrations for current internal tournament
  const fetchRegistrations = async () => {
    if (!selectedInternalTournament) {
      setInternalRegistrations([]);
      return;
    }
    try {
      const resp = await axios.get(API_ENDPOINTS.INTERNAL_TOURNAMENT_REGISTRATIONS, {
        params: {
          tanggal: selectedInternalTournament.date,
          tempat: selectedInternalTournament.location,
        },
      });
      setInternalRegistrations(Array.isArray(resp.data) ? resp.data : []);
    } catch (err) {
      console.error('Error fetching internal registrations:', err);
      setInternalRegistrations([]);
    }
  };

  useEffect(() => {
    fetchRegistrations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedInternalTournament]);

  // Kunci form turnamen internal jika user belum login / email belum terbaca
  const isInternalTournamentFormLocked =
    showOnlyTournament && (!user || !user.email);

  // Handle hash URL for direct linking to ranked match tab
  useEffect(() => {
    const handleHashChange = () => {
      if (window.location.hash === '#ranked-match') {
        setMainTab(1); // Switch to ranked match tab
      }
    };

    // Check hash on mount
    handleHashChange();

    // Listen for hash changes
    window.addEventListener('hashchange', handleHashChange);
    
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  // Set default view based on screen size
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 900) { // md breakpoint
        setIsMobileView(false); // Table view for desktop
      } else {
        setIsMobileView(true); // Card view for mobile
      }
    };

    // Set initial view
    handleResize();

    // Listen for resize events
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const handleMainTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setMainTab(newValue);
  };

  // Fetch Liga standings
  useEffect(() => {
    const fetchLiga = async () => {
      if (mainTab !== 2) return; // Only fetch when Liga tab active
      setLigaLoading(true);
      setLigaError(null);
      try {
        const sheet = ligaLocation; // sheet name equals location label for now
        const res = await fetchWithBaseURL(`/api/bookings?action=liga-standings&sheet=${encodeURIComponent(sheet)}`);
        if (!res.ok) throw new Error('Gagal memuat klasemen liga');
        const data = await res.json();
        setLigaData(Array.isArray(data) ? data : []);
      } catch (e) {
        setLigaError('Gagal memuat klasemen liga');
        setLigaData([]);
      } finally {
        setLigaLoading(false);
      }
    };
    fetchLiga();
  }, [mainTab, ligaLocation]);

  // Handle player click to show detailed stats
  const handlePlayerClick = (player: any) => {
    const urlName = getPlayerUrlName(player.nama);
    // Pastikan tab ranked match aktif
    setMainTab(1);
    // Ubah URL
    navigate(`/ranked-match/${urlName}`);
    // Langsung set player dan buka modal untuk UX yang lebih baik (tanpa delay)
    setSelectedPlayer(player);
    setPlayerStatsModalOpen(true);
  };

  // Fungsi untuk mengubah nama menjadi URL-friendly (lowercase, replace spasi dengan dash)
  const getPlayerUrlName = (nama: string) => {
    return nama.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
  };


  // Effect untuk membaca parameter URL dan membuka modal statistik
  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // Fungsi untuk mencari pemain berdasarkan URL name
    const findPlayerByUrlName = (urlName: string) => {
      const allRankedData = [
        ...rankedMatchData.ranked_single,
        ...rankedMatchData.ranked_double_solo,
        ...rankedMatchData.ranked_double_party
      ];
      
      return allRankedData.find((player: any) => {
        const playerUrlName = getPlayerUrlName(player.nama);
        return playerUrlName === urlName.toLowerCase();
      });
    };
    
    // Jika tidak ada playerName di URL, pastikan modal tertutup dan clear selectedPlayer
    if (!playerName) {
      setPlayerStatsModalOpen(false);
      setSelectedPlayer(null);
      return;
    }

    // Tunggu sampai data selesai dimuat
    if (playerName && !rankedMatchLoading && rankedMatchData) {
      // Pastikan data sudah dimuat
      const hasData = rankedMatchData.ranked_single.length > 0 || 
                     rankedMatchData.ranked_double_solo.length > 0 || 
                     rankedMatchData.ranked_double_party.length > 0;
      
      if (hasData) {
        const player = findPlayerByUrlName(playerName);
        if (player) {
          // Set player dan buka modal
          setSelectedPlayer(player);
          setPlayerStatsModalOpen(true);
          // Pastikan tab ranked match aktif
          setMainTab(1);
        } else {
          // Jika pemain tidak ditemukan, redirect ke /ranked-match
          navigate('/ranked-match', { replace: true });
        }
      }
    }
  }, [playerName, rankedMatchData, rankedMatchLoading, navigate]);

  // Handle close modal dan update URL
  const handleClosePlayerStatsModal = () => {
    // Ubah URL kembali ke /ranked-match tanpa parameter terlebih dahulu
    // useEffect akan otomatis menutup modal ketika playerName menjadi undefined
    if (playerName) {
      navigate('/ranked-match', { replace: true });
    } else {
      // Jika tidak ada playerName, langsung tutup modal
      setPlayerStatsModalOpen(false);
      setSelectedPlayer(null);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Hitung klasemen liga: urutkan berdasarkan poin desc dan terapkan competition ranking (tie -> rank sama)
  const getLigaComputedData = () => {
    const sorted = [...ligaData].sort((a, b) => (b.poin || 0) - (a.poin || 0));
    // Dense ranking: 1,1,2,3,...
    let currentRank = 0;
    let lastPoints: number | null = null;
    return sorted.map((row) => {
      const points = row.poin || 0;
      if (lastPoints === null || points !== lastPoints) {
        currentRank = currentRank + 1;
        lastPoints = points;
      }
      return { ...row, computedRank: currentRank };
    });
  };






  // Handle reset season
  const handleResetSeason = async () => {
    if (resetPassword !== '123117') {
      setSubmitError('Password salah');
      return;
    }

    setResetting(true);
    setSubmitError(null);
    
    try {
      const response = await axios.post('/api/bookings?action=reset-season', {
        password: resetPassword
      });
      
      if (response.data.success) {
        setResetSeasonDialogOpen(false);
        setResetPassword('');
        // Refresh data setelah reset
        const fetchRankedMatchData = async () => {
          try {
            const dataResponse = await axios.get('/api/bookings?action=ranked-leaderboard');
            if (dataResponse.status === 200) {
              setRankedMatchData(dataResponse.data);
            }
          } catch (error) {
            console.error('Error refreshing data:', error);
          }
        };
        fetchRankedMatchData();
      } else {
        setSubmitError(response.data.message || 'Gagal reset season');
      }
    } catch (error: any) {
      setSubmitError(error.response?.data?.message || 'Terjadi kesalahan saat reset season');
    } finally {
      setResetting(false);
    }
  };



  // Get ranked match data based on selected category
  const getRankedMatchData = () => {
    let data: any[] = [];
    switch (selectedCategory) {
      case 'tunggal':
        data = rankedMatchData.ranked_single;
        break;
      case 'ganda_solo':
        data = rankedMatchData.ranked_double_solo;
        break;
      case 'ganda_party':
        data = rankedMatchData.ranked_double_party;
        break;
      default:
        data = rankedMatchData.ranked_double_solo;
    }

    // Filter berdasarkan level yang dipilih terlebih dahulu
    let filteredData = data;
    if (selectedLevel !== 'Gabungan') {
      filteredData = data.filter((player: any) => {
        const playerLevel = String(player.level || '').toLowerCase();
        const selected = String(selectedLevel || '').toLowerCase();

        // Normalisasi selected level ke kategori canonical
        let canonical: 'beginner' | 'intermediate' | 'advanced' | null = null;
        if (selected.includes('beginner') || selected.includes('pemula')) canonical = 'beginner';
        else if (selected.includes('intermediate') || selected.includes('menengah')) canonical = 'intermediate';
        else if (selected.includes('advanced') || selected.includes('lanjutan') || selected.includes('mahir')) canonical = 'advanced';

        if (!canonical) return false;

        // Cek kecocokan level pemain terhadap canonical + sinonim lokal
        if (canonical === 'beginner') {
          return playerLevel.includes('beginner') || playerLevel.includes('pemula');
        }
        if (canonical === 'intermediate') {
          return playerLevel.includes('intermediate') || playerLevel.includes('menengah');
        }
        // advanced
        return (
          playerLevel.includes('advanced') ||
          playerLevel.includes('lanjutan') ||
          playerLevel.includes('mahir')
        );
      });
    }

    // Urutkan berdasarkan poin (desc) dan tetapkan rank dengan tie (poin sama -> rank sama)
    const sortedByPoints = [...filteredData].sort((a: any, b: any) => (b.poin || 0) - (a.poin || 0));

    // Dense ranking: 1,1,2,3,... (tanpa loncat angka saat ada tie)
    let currentRank = 0;
    let lastPoints: number | null = null;

    const dataWithLevelRank = sortedByPoints.map((player: any) => {
      const playerPoints = player.poin || 0;

      if (lastPoints === null || playerPoints !== lastPoints) {
        currentRank = currentRank + 1; // naik satu per kelompok poin baru
        lastPoints = playerPoints;
      }

      const originalIndex = data.findIndex((p: any) => p.nama === player.nama);
      return {
        ...player,
        levelRank: currentRank,
        originalRank: originalIndex >= 0 ? originalIndex + 1 : currentRank
      };
    });

    // Filter berdasarkan pencarian nama
    let finalData = dataWithLevelRank;
    if (searchQuery.trim()) {
      finalData = dataWithLevelRank.filter((player: any) => {
        const playerName = (player.nama || '').toLowerCase();
        const query = searchQuery.toLowerCase().trim();
        
        // Cek apakah nama cocok dengan pencarian
        const nameMatches = playerName.includes(query);
        
        // Jika nama cocok, cek apakah pemain di-mask atau tidak
        if (nameMatches) {
          // Jika win rate >= 50%, nama tidak di-mask, jadi bisa dicari
          if (player.winRate >= 50) {
            return true;
          }
          // Jika win rate < 50%, nama di-mask, jadi tidak bisa dicari
          else {
            return false;
          }
        }
        
        return false;
      });
    }

    return finalData;
  };

  const getCategoryLabel = () => {
    switch (selectedCategory) {
      case 'tunggal':
        return 'Tunggal';
      case 'ganda_solo':
        return 'Ganda (Solo)';
      case 'ganda_party':
        return 'Ganda (Party)';
      default:
        return 'Ganda (Solo)';
    }
  };

  // Fungsi untuk masking nama berdasarkan win rate
  const getMaskedName = (nama: string, winRate: number) => {
    if (winRate >= 50) {
      return nama; // Tampilkan nama lengkap jika win rate >= 50%
    }
    
    // Masking untuk win rate < 50%
    if (nama.length <= 2) {
      return '***'; // Jika nama terlalu pendek, mask semua
    }
    
    // Tampilkan huruf pertama + asterisk
    return nama.charAt(0) + '***';
  };

  // Function to highlight search query in text
  const highlightSearchQuery = (text: string, query: string) => {
    if (!query.trim()) return text;
    
    const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => 
      regex.test(part) ? (
        <Box
          key={index}
          component="span"
          sx={{
            backgroundColor: 'warning.light',
            color: 'warning.contrastText',
            fontWeight: 600,
            px: 0.5,
            borderRadius: 0.5
          }}
        >
          {part}
        </Box>
      ) : part
    );
  };

  // Get autocomplete options based on category
  // Share functionality
  const handleShareClick = () => {
    setShareDialogOpen(true);
  };

  const handleCloseShareDialog = () => {
    setShareDialogOpen(false);
  };

  const generateShareText = (tournament: any, registrations: any[]) => {
    // Generate player list text
    const paidList = registrations.filter(r =>
      String(r.statusBayar || '').toLowerCase().includes('sudah')
    );
    const unpaidList = registrations.filter(r =>
      !String(r.statusBayar || '').toLowerCase().includes('sudah')
    );
    
    const sortFn = (a: any, b: any) => {
      const hasPartnerA = a.namaP2 && a.namaP2.trim() !== '';
      const hasPartnerB = b.namaP2 && b.namaP2.trim() !== '';
      
      if (hasPartnerA && !hasPartnerB) return -1;
      if (!hasPartnerA && hasPartnerB) return 1;
      
      const timeA = new Date(a.timestamp || 0).getTime();
      const timeB = new Date(b.timestamp || 0).getTime();
      return timeA - timeB;
    };
    
    paidList.sort(sortFn);
    unpaidList.sort(sortFn);
    
    const slotCapacity = parseInt(tournament.slot || '0');
    const remainingSlots = slotCapacity - paidList.length;
    const registeredUnpaid = unpaidList.filter((_, index) => index < remainingSlots);
    const waitingList = unpaidList.filter((_, index) => index >= remainingSlots);
    const registeredPlayers = [...paidList, ...registeredUnpaid];
    
    let playerListText = '';
    
    if (registeredPlayers.length > 0) {
      playerListText += `\n👥 *Pemain Terdaftar (${registeredPlayers.length}/${tournament.slot}):*\n`;
      registeredPlayers.forEach((r, index) => {
        const hasPartner = r.namaP2 && r.namaP2.trim() !== '';
        const isPaid = String(r.statusBayar || '').toLowerCase().includes('sudah');
        const status = isPaid ? '✅' : '⏳';
        playerListText += `${index + 1}. ${status} ${r.namaP1}${hasPartner ? ` / ${r.namaP2}` : ' / Partner'}\n`;
      });
    }
    
    if (waitingList.length > 0) {
      playerListText += `\n⏳ *Waiting List (${waitingList.length}):*\n`;
      waitingList.forEach((r, index) => {
        const hasPartner = r.namaP2 && r.namaP2.trim() !== '';
        playerListText += `${index + 1}. ${r.namaP1}${hasPartner ? ` / ${r.namaP2}` : ' / Partner'}\n`;
      });
    }
    
    // Remove trailing newlines and add exactly one
    playerListText = playerListText.trimEnd() + '\n';

    return `🏓 *${tournament.name}*

📅 Tanggal: ${tournament.date}
⏰ Waktu: ${tournament.time}
📍 Tempat: ${tournament.location}
${tournament.address ? `🗺️ Alamat: ${tournament.address.replace(/\(https?:\/\/[^)]+\)/, '')}` : ''}
💰 Biaya: Rp ${tournament.fee}
🎯 Slot: ${tournament.slot}

${tournament.description ? `📋 ${tournament.description.replace(/\n/g, '\n')}` : ''}${playerListText ? `\n${playerListText}` : ''}
🔗 Daftar sekarang di: ${window.location.href}`;
  };

  const showSnackbar = (message: string) => {
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const captureTableScreenshot = async (): Promise<string> => {
    // Pilih elemen target secara robust
    const targetRef = isMobileView ? cardViewRef : tableRef;
    let targetEl: HTMLElement | null = targetRef.current as unknown as HTMLElement | null;
    if (!targetEl) {
      // Fallback: cari by ID
      targetEl = document.getElementById('ranked-leaderboard-wrapper');
    }
    if (!targetEl) return '';
    
    try {
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(targetEl, {
        backgroundColor: '#1a1a1a',
        scale: 2,
        useCORS: true,
        allowTaint: true
      });
      return canvas.toDataURL('image/png');
    } catch (error) {
      console.error('Error capturing screenshot:', error);
      return '';
    }
  };

  const createLeaderboardShareableImage = async (screenshot: string, text: string): Promise<string> => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        resolve('');
        return;
      }

      // Set canvas size
      canvas.width = 800;
      canvas.height = 1000;

      // Create background
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Add screenshot if available
      if (screenshot) {
        const img = new Image();
        img.onload = () => {
          // Resize and center the screenshot
          const maxWidth = 700;
          const maxHeight = 600;
          const ratio = Math.min(maxWidth / img.width, maxHeight / img.height);
          const newWidth = img.width * ratio;
          const newHeight = img.height * ratio;
          
          const x = (canvas.width - newWidth) / 2;
          const y = 50;
          
          ctx.drawImage(img, x, y, newWidth, newHeight);
          
          // Add text below screenshot
          ctx.fillStyle = '#333333';
          ctx.font = 'bold 24px Arial';
          ctx.textAlign = 'center';
          ctx.fillText('🏆 Leaderboard RaketerS', canvas.width / 2, y + newHeight + 80);
          
          ctx.font = '18px Arial';
          const lines = text.split('\n');
          let textY = y + newHeight + 120;
          
          lines.forEach(line => {
            if (line.trim()) {
              ctx.fillText(line, canvas.width / 2, textY);
              textY += 30;
            }
          });
          
          // Add website URL
          ctx.fillStyle = '#25D366';
          ctx.font = 'bold 16px Arial';
          ctx.fillText('🔗 raketers.com', canvas.width / 2, textY + 40);
          
          resolve(canvas.toDataURL('image/png'));
        };
        img.src = screenshot;
      } else {
        // If no screenshot, just add text
        ctx.fillStyle = '#333333';
        ctx.font = 'bold 24px Arial';
        ctx.textAlign = 'center';
        ctx.fillText('🏆 Leaderboard RaketerS', canvas.width / 2, 100);
        
        ctx.font = '18px Arial';
        const lines = text.split('\n');
        let textY = 150;
        
        lines.forEach(line => {
          if (line.trim()) {
            ctx.fillText(line, canvas.width / 2, textY);
            textY += 30;
          }
        });
        
        // Add website URL
        ctx.fillStyle = '#25D366';
        ctx.font = 'bold 16px Arial';
        ctx.fillText('🔗 raketers.com', canvas.width / 2, textY + 40);
        
        resolve(canvas.toDataURL('image/png'));
      }
    });
  };

  const shareToWhatsApp = async () => {
    const screenshot = await captureTableScreenshot();
    const leaderboardUrl = `${window.location.origin}${window.location.pathname}#ranked-match`;
    const text = `🏆 *Ranked Match Leaderboard - ${getCategoryLabel()}*\n\nLihat peringkat terbaru di RaketerS! 🏸\n\n🔗 ${leaderboardUrl}\n\n#RaketerS #Badminton #Leaderboard`;
    
    try {
      // Create shareable image
      const shareableImage = await createLeaderboardShareableImage(screenshot, text);
      
      if (shareableImage) {
        // Download the image
        const link = document.createElement('a');
        link.download = `leaderboard-${getCategoryLabel().toLowerCase().replace(/\s+/g, '-')}.png`;
        link.href = shareableImage;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Open WhatsApp with text
        const whatsappText = `${text}\n\n🔗 ${leaderboardUrl}`;
        const url = `https://wa.me/?text=${encodeURIComponent(whatsappText)}`;
        window.open(url, '_blank');
        
        // Show success message
        showSnackbar('Gambar telah didownload! Silakan upload gambar tersebut ke WhatsApp bersama dengan teks yang sudah disiapkan.');
      } else {
        // Fallback to text only
        if (navigator.share) {
          navigator.share({
            title: `Ranked Match Leaderboard`,
            text: text,
            url: window.location.href
          });
        } else {
          navigator.clipboard.writeText(text);
          alert('Teks leaderboard telah disalin ke clipboard!');
        }
      }
    } catch (error) {
      console.error('Error sharing to WhatsApp:', error);
      // Fallback to text only
      if (navigator.share) {
        navigator.share({
          title: `Ranked Match Leaderboard`,
          text: text,
          url: window.location.href
        });
      } else {
        navigator.clipboard.writeText(text);
        alert('Teks leaderboard telah disalin ke clipboard!');
      }
    }
  };

  const shareToTwitter = async () => {
    const leaderboardUrl = `${window.location.origin}${window.location.pathname}#ranked-match`;
    const text = `🏆 Ranked Match Leaderboard - ${getCategoryLabel()} di RaketerS! 🏸\n\nLihat peringkat terbaru dan bersaing untuk menjadi yang terbaik!\n\n🔗 ${leaderboardUrl}\n\n#RaketerS #Badminton #Leaderboard`;
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank');
    showSnackbar('Membuka Twitter...');
    handleCloseShareDialog();
  };

  const shareToFacebook = async () => {
    const leaderboardUrl = `${window.location.origin}${window.location.pathname}#ranked-match`;
    const text = `🏆 Ranked Match Leaderboard - ${getCategoryLabel()} di RaketerS! 🏸\n\nLihat peringkat terbaru dan bersaing untuk menjadi yang terbaik!\n\n🔗 ${leaderboardUrl}`;
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(leaderboardUrl)}&quote=${encodeURIComponent(text)}`;
    window.open(url, '_blank');
    showSnackbar('Membuka Facebook...');
    handleCloseShareDialog();
  };

  const shareToTelegram = async () => {
    const leaderboardUrl = `${window.location.origin}${window.location.pathname}#ranked-match`;
    const text = `🏆 *Ranked Match Leaderboard - ${getCategoryLabel()}*\n\nLihat peringkat terbaru di RaketerS! 🏸\n\n🔗 ${leaderboardUrl}\n\n#RaketerS #Badminton #Leaderboard`;
    const url = `https://t.me/share/url?url=${encodeURIComponent(leaderboardUrl)}&text=${encodeURIComponent(text)}`;
    window.open(url, '_blank');
    showSnackbar('Membuka Telegram...');
    handleCloseShareDialog();
  };

  const copyToClipboard = async () => {
    const leaderboardUrl = `${window.location.origin}${window.location.pathname}#ranked-match`;
    const text = `🏆 Ranked Match Leaderboard - ${getCategoryLabel()}\n\nLihat peringkat terbaru di RaketerS! 🏸\n\n🔗 ${leaderboardUrl}\n\n#RaketerS #Badminton #Leaderboard`;
    
    try {
      await navigator.clipboard.writeText(text);
      showSnackbar('Teks berhasil disalin ke clipboard!');
    } catch (error) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      showSnackbar('Teks berhasil disalin ke clipboard!');
    }
    handleCloseShareDialog();
  };

  const downloadScreenshot = async () => {
    const screenshot = await captureTableScreenshot();
    if (screenshot) {
      const link = document.createElement('a');
      link.download = `raketers-leaderboard-${getCategoryLabel().toLowerCase().replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.png`;
      link.href = screenshot;
      link.click();
      showSnackbar('Screenshot berhasil diunduh!');
    } else {
      showSnackbar('Gagal mengambil screenshot');
    }
    handleCloseShareDialog();
  };

  // Komponen untuk menampilkan sponsor di sidebar
  const SponsorSidebar: React.FC<{ sponsors: Sponsor[]; position: 'left' | 'right' }> = ({ sponsors, position }) => {
    if (sponsors.length === 0) return null;
    
    return (
      <Box
        sx={{
          display: { xs: 'none', lg: 'flex' },
          flexDirection: 'column',
          gap: 1,
          alignItems: 'center',
          justifyContent: 'flex-start',
          position: 'fixed',
          ...(position === 'left'
            ? { left: { xs: 0, lg: 6, xl: 16 } }
            : { right: { xs: 0, lg: 6, xl: 16 } }),
          top: 100,
          maxHeight: 'calc(100vh - 120px)',
          overflowY: 'auto',
          width: { lg: 120, xl: 170 },
          zIndex: 100,
          px: { lg: 1, xl: 2 },
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
        }}
      >
        {sponsors.map((sponsor, index) => (
          <Box
            key={sponsor.id}
            component="img"
            src={sponsor.url}
            alt={`Sponsor ${index + 1}`}
            sx={{
              height: 'auto',
              width: '100%',
              maxWidth: { lg: 120, xl: 140 },
              objectFit: 'contain',
              boxShadow: mode === 'light'
                ? '0 8px 32px rgba(25,118,210,0.15)'
                : '0 8px 32px rgba(13,71,161,0.3)',
              borderRadius: 3,
              background: mode === 'light'
                ? 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)'
                : 'linear-gradient(135deg, rgba(13,71,161,0.1) 0%, rgba(21,101,192,0.05) 100%)',
              backdropFilter: 'blur(10px)',
              border: mode === 'light'
                ? '0.5px solid rgba(255,255,255,0.4)'
                : '0.5px solid rgba(255,255,255,0.2)',
              p: 1,
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'scale(1.05)',
                boxShadow: mode === 'light'
                  ? '0 12px 40px rgba(25,118,210,0.2)'
                  : '0 12px 40px rgba(13,71,161,0.4)',
              }
            }}
          />
        ))}
      </Box>
    );
  };

  return (
    <Box sx={{ width: '100%', mt: 4, position: 'relative' }}>
      <Helmet>
        <title>{pageHeading}</title>
      </Helmet>
      
      {/* Sponsor Sidebar Kiri - Desktop Only - Hide for tournament page */}
      {!showOnlyTournament && <SponsorSidebar sponsors={sponsors} position="left" />}
      
      {/* Sponsor Sidebar Kanan - Desktop Only - Hide for tournament page */}
      {!showOnlyTournament && <SponsorSidebar sponsors={sponsors} position="right" />}

      {/* Konten Utama */}
      <Box sx={{ width: '100%', px: showOnlyTournament ? 0 : { xs: 0.5, sm: 1, md: 1.5 } }}>
        <Typography
          variant="h4"
          component="h1"
          align="center"
          gutterBottom
          sx={{ fontWeight: 'bold' }}
        >
          {pageHeading}
        </Typography>
      
      {/* Status Login */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
        {user ? (
          <Chip 
            label={`Logged in as: ${user.email} ${isAdmin ? '(Admin)' : '(User)'}`}
            color={isAdmin ? "success" : "default"}
            variant="outlined"
          />
        ) : (
          <Chip 
            label="Not logged in (View Only)"
            color="default"
            variant="outlined"
          />
        )}
      </Box>

      {!showOnlyRanked && !showOnlyTournament && !showOnlyLiga && (
        <Tabs value={mainTab} onChange={handleMainTabChange} centered>
          <Tab label="Turnamen" />
          <Tab label="Ranked Match" />
          <Tab label="Liga" />
        </Tabs>
      )}
      
      {/* Tab Turnamen */}
      {!showOnlyRanked && !showOnlyLiga && (
      <TabPanel value={mainTab} index={0}>
        {/* Pendaftaran Turnamen Internal - hanya tampil di halaman /internal-tournament */}
        {showOnlyTournament && (
          <Box
            sx={{
              mb: 4,
              p: { xs: 1.5, sm: 2, md: 2.5 },
              borderRadius: 3,
              background: (theme) =>
                theme.palette.mode === 'dark'
                  ? 'linear-gradient(135deg, rgba(13,71,161,0.28) 0%, rgba(21,101,192,0.16) 100%)'
                  : 'rgba(255,255,255,0.96)',
              boxShadow: (theme) =>
                theme.palette.mode === 'dark'
                  ? '0 12px 32px rgba(0,0,0,0.7)'
                  : '0 10px 26px rgba(25,118,210,0.15)',
              border: (theme) =>
                theme.palette.mode === 'dark'
                  ? '1px solid rgba(99,170,255,0.45)'
                  : '1px solid rgba(255,255,255,0.9)',
              width: '100%',
              maxWidth: '100%'
            }}
          >
            <Box sx={{ textAlign: 'center', mb: { xs: 2, sm: 3 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: { xs: 1, sm: 1.5 }, mb: 1 }}>
                <TrophyIcon sx={{ 
                  fontSize: { xs: 28, sm: 32 }, 
                  color: (theme) => theme.palette.mode === 'dark' ? '#63aaff' : '#1976d2' 
                }} />
                <Typography variant="h5" fontWeight={800} sx={{ 
                  color: (theme) => theme.palette.mode === 'dark' ? '#63aaff' : '#1976d2',
                  fontSize: { xs: '1.25rem', sm: '1.5rem' }
                }}>
                  Pendaftaran Turnamen Internal
                </Typography>
              </Box>
            </Box>

        {internalTournaments.length === 0 ? (
              <Typography variant="body2" align="center" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
                Konfigurasi turnamen internal belum tersedia.
              </Typography>
            ) : (
              <>
                {activeInternalTournament && (
                  <>
                    {internalTournaments.length > 1 && (
                      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
                        <FormControl
                          size="small"
                          sx={{
                            minWidth: 260,
                            backgroundColor: (theme) =>
                              theme.palette.mode === 'dark' ? 'rgba(13,71,161,0.5)' : 'rgba(255,255,255,0.95)',
                            borderRadius: 2,
                            boxShadow: (theme) =>
                              theme.palette.mode === 'dark'
                                ? '0 6px 18px rgba(0,0,0,0.7)'
                                : '0 6px 18px rgba(25,118,210,0.18)',
                          }}
                        >
                          <InputLabel id="internal-tournament-select-label">Pilih Turnamen</InputLabel>
                          <Select
                            labelId="internal-tournament-select-label"
                            label="Pilih Turnamen"
                            value={
                              selectedInternalTournamentId ??
                              activeInternalTournament.id
                            }
                            onChange={(e) =>
                              setSelectedInternalTournamentId(Number(e.target.value))
                            }
                          >
                            {internalTournaments.map((t) => (
                              <MenuItem key={t.id} value={t.id}>
                                {shortenTournamentName(t.name)} — {t.date}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                    )}

                    <Box key={activeInternalTournament.id} sx={{ mb: 3 }}>
                      {/* Tournament Header with Icon */}
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 2, 
                        mb: 3,
                        pb: 2,
                        borderBottom: '2px solid',
                        borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(99,170,255,0.3)' : 'rgba(25,118,210,0.2)'
                      }}>
                        <Box sx={{
                          width: 60,
                          height: 60,
                          borderRadius: 2,
                          background: (theme) => theme.palette.mode === 'dark' 
                            ? 'linear-gradient(135deg, rgba(99,170,255,0.3) 0%, rgba(25,118,210,0.2) 100%)'
                            : 'linear-gradient(135deg, rgba(25,118,210,0.15) 0%, rgba(13,71,161,0.1) 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: (theme) => theme.palette.mode === 'dark' 
                            ? '1px solid rgba(99,170,255,0.4)' 
                            : '1px solid rgba(25,118,210,0.3)'
                        }}>
                          <SportsIcon sx={{ 
                            fontSize: 36, 
                            color: (theme) => theme.palette.mode === 'dark' ? '#63aaff' : '#1976d2' 
                          }} />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="h5" fontWeight={800} sx={{ 
                            color: (theme) => theme.palette.mode === 'dark' ? '#63aaff' : '#1565c0',
                            mb: 0.5,
                            fontSize: { xs: '1.1rem', sm: '1.25rem' }
                          }}>
                            {activeInternalTournament.name}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 } }}>
                          <IconButton
                            onClick={() => {
                              const shareText = generateShareText(activeInternalTournament, internalRegistrations);
                              try {
                                navigator.clipboard.writeText(shareText);
                                showSnackbar('Teks berhasil disalin ke clipboard!');
                              } catch (error) {
                                // Fallback for older browsers
                                const textArea = document.createElement('textarea');
                                textArea.value = shareText;
                                textArea.style.position = 'fixed';
                                textArea.style.left = '-999999px';
                                document.body.appendChild(textArea);
                                textArea.select();
                                document.execCommand('copy');
                                document.body.removeChild(textArea);
                                showSnackbar('Teks berhasil disalin ke clipboard!');
                              }
                            }}
                            sx={{
                              background: (theme) => theme.palette.mode === 'dark' 
                                ? 'rgba(99,170,255,0.15)' 
                                : 'rgba(25,118,210,0.1)',
                              '&:hover': {
                                background: (theme) => theme.palette.mode === 'dark' 
                                  ? 'rgba(99,170,255,0.25)' 
                                  : 'rgba(25,118,210,0.2)',
                              }
                            }}
                          >
                            <CopyIcon sx={{ 
                              fontSize: { xs: 20, sm: 24 }, 
                              color: (theme) => theme.palette.mode === 'dark' ? '#63aaff' : '#1976d2' 
                            }} />
                          </IconButton>
                          <IconButton
                            onClick={() => {
                              const shareText = generateShareText(activeInternalTournament, internalRegistrations);
                              const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
                              window.open(whatsappUrl, '_blank');
                            }}
                            sx={{
                              background: (theme) => theme.palette.mode === 'dark' 
                                ? 'rgba(99,170,255,0.15)' 
                                : 'rgba(25,118,210,0.1)',
                              '&:hover': {
                                background: (theme) => theme.palette.mode === 'dark' 
                                  ? 'rgba(99,170,255,0.25)' 
                                  : 'rgba(25,118,210,0.2)',
                              }
                            }}
                          >
                            <WhatsAppIcon sx={{ 
                              fontSize: { xs: 20, sm: 24 }, 
                              color: (theme) => theme.palette.mode === 'dark' ? '#63aaff' : '#1976d2' 
                            }} />
                          </IconButton>
                        </Box>
                      </Box>

                      {/* Info Grid */}
                      <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mb: 3 }}>
                        <Grid item xs={6} sm={6} md={3}>
                          <Card sx={{
                            background: (theme) => theme.palette.mode === 'dark' 
                              ? 'rgba(99,170,255,0.08)' 
                              : 'rgba(25,118,210,0.05)',
                            border: (theme) => theme.palette.mode === 'dark' 
                              ? '1px solid rgba(99,170,255,0.25)' 
                              : '1px solid rgba(25,118,210,0.15)',
                            borderRadius: 2,
                            height: '100%'
                          }}>
                            <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 }, mb: 1 }}>
                                <CalendarIcon sx={{ 
                                  fontSize: { xs: 18, sm: 20 }, 
                                  color: (theme) => theme.palette.mode === 'dark' ? '#63aaff' : '#1976d2' 
                                }} />
                                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                  TANGGAL
                                </Typography>
                              </Box>
                              <Typography variant="body1" fontWeight={700} sx={{ fontSize: { xs: '0.85rem', sm: '1rem' } }}>
                                {activeInternalTournament.date}
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>

                        <Grid item xs={6} sm={6} md={3}>
                          <Card sx={{
                            background: (theme) => theme.palette.mode === 'dark' 
                              ? 'rgba(99,170,255,0.08)' 
                              : 'rgba(25,118,210,0.05)',
                            border: (theme) => theme.palette.mode === 'dark' 
                              ? '1px solid rgba(99,170,255,0.25)' 
                              : '1px solid rgba(25,118,210,0.15)',
                            borderRadius: 2,
                            height: '100%'
                          }}>
                            <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 }, mb: 1 }}>
                                <TimeIcon sx={{ 
                                  fontSize: { xs: 18, sm: 20 }, 
                                  color: (theme) => theme.palette.mode === 'dark' ? '#63aaff' : '#1976d2' 
                                }} />
                                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                  WAKTU
                                </Typography>
                              </Box>
                              <Typography variant="body1" fontWeight={700} sx={{ fontSize: { xs: '0.85rem', sm: '1rem' } }}>
                                {activeInternalTournament.time}
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>

                        <Grid item xs={6} sm={6} md={3}>
                          <Card sx={{
                            background: (theme) => theme.palette.mode === 'dark' 
                              ? 'rgba(99,170,255,0.08)' 
                              : 'rgba(25,118,210,0.05)',
                            border: (theme) => theme.palette.mode === 'dark' 
                              ? '1px solid rgba(99,170,255,0.25)' 
                              : '1px solid rgba(25,118,210,0.15)',
                            borderRadius: 2,
                            height: '100%'
                          }}>
                            <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 }, mb: 1 }}>
                                <LocationIcon sx={{ 
                                  fontSize: { xs: 18, sm: 20 }, 
                                  color: (theme) => theme.palette.mode === 'dark' ? '#63aaff' : '#1976d2' 
                                }} />
                                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                  TEMPAT
                                </Typography>
                              </Box>
                              <Typography variant="body1" fontWeight={700} sx={{ fontSize: { xs: '0.85rem', sm: '1rem' } }}>
                                {activeInternalTournament.location}
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>

                        <Grid item xs={6} sm={6} md={3}>
                          <Card sx={{
                            background: (theme) => theme.palette.mode === 'dark' 
                              ? 'rgba(99,170,255,0.08)' 
                              : 'rgba(25,118,210,0.05)',
                            border: (theme) => theme.palette.mode === 'dark' 
                              ? '1px solid rgba(99,170,255,0.25)' 
                              : '1px solid rgba(25,118,210,0.15)',
                            borderRadius: 2,
                            height: '100%'
                          }}>
                            <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 }, mb: 1 }}>
                                <FeeIcon sx={{ 
                                  fontSize: { xs: 18, sm: 20 }, 
                                  color: (theme) => theme.palette.mode === 'dark' ? '#63aaff' : '#1976d2' 
                                }} />
                                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                  BIAYA
                                </Typography>
                              </Box>
                              <Typography variant="body1" fontWeight={700} sx={{ fontSize: { xs: '0.85rem', sm: '1rem' } }}>
                                Rp {activeInternalTournament.fee}
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      </Grid>

                      {/* Address */}
                      {activeInternalTournament.address && (
                        <Card sx={{
                          mb: 3,
                          background: (theme) => theme.palette.mode === 'dark' 
                            ? 'rgba(99,170,255,0.05)' 
                            : 'rgba(25,118,210,0.03)',
                          border: (theme) => theme.palette.mode === 'dark' 
                            ? '1px solid rgba(99,170,255,0.2)' 
                            : '1px solid rgba(25,118,210,0.1)',
                          borderRadius: 2
                        }}>
                          <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: { xs: 1, sm: 1.5 } }}>
                              <LocationIcon sx={{ 
                                fontSize: { xs: 16, sm: 18 }, 
                                color: (theme) => theme.palette.mode === 'dark' ? '#63aaff' : '#1976d2',
                                mt: 0.3
                              }} />
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block', fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                  ALAMAT LENGKAP
                                </Typography>
                                <Typography variant="body2" sx={{ lineHeight: 1.5, fontSize: { xs: '0.85rem', sm: '0.9rem' } }}>
                                  {renderAddressWithMapLink(activeInternalTournament.address)}
                                </Typography>
                              </Box>
                            </Box>
                          </CardContent>
                        </Card>
                      )}

                      {/* Slot Progress */}
                      {activeInternalTournament.slot && (
                        <Card sx={{
                          mb: 3,
                          background: (theme) => theme.palette.mode === 'dark' 
                            ? 'rgba(99,170,255,0.05)' 
                            : 'rgba(25,118,210,0.03)',
                          border: (theme) => theme.palette.mode === 'dark' 
                            ? '1px solid rgba(99,170,255,0.2)' 
                            : '1px solid rgba(25,118,210,0.1)',
                          borderRadius: 2
                        }}>
                          <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 } }}>
                                <TeamIcon sx={{ 
                                  fontSize: { xs: 18, sm: 20 }, 
                                  color: (theme) => theme.palette.mode === 'dark' ? '#63aaff' : '#1976d2' 
                                }} />
                                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                  SLOT TERSEDIA
                                </Typography>
                              </Box>
                              <Typography variant="h6" fontWeight={800} sx={{ 
                                color: (theme) => theme.palette.mode === 'dark' ? '#63aaff' : '#1976d2',
                                fontSize: { xs: '1rem', sm: '1.25rem' }
                              }}>
                                {(() => {
                                  const slotCapacity = parseInt(activeInternalTournament.slot || '0');
                                  const paidList = internalRegistrations.filter(r =>
                                    String(r.statusBayar || '').toLowerCase().includes('sudah')
                                  );
                                  const unpaidList = internalRegistrations.filter(r =>
                                    !String(r.statusBayar || '').toLowerCase().includes('sudah')
                                  );
                                  const remainingSlots = slotCapacity - paidList.length;
                                  const registeredUnpaid = unpaidList.filter((_, index) => index < remainingSlots);
                                  const totalRegistered = paidList.length + registeredUnpaid.length;
                                  return `${totalRegistered}/${activeInternalTournament.slot}`;
                                })()}
                              </Typography>
                            </Box>
                            <LinearProgress 
                              variant="determinate"
                              value={(() => {
                                const slotCapacity = parseInt(activeInternalTournament.slot || '0');
                                const paidList = internalRegistrations.filter(r =>
                                  String(r.statusBayar || '').toLowerCase().includes('sudah')
                                );
                                const unpaidList = internalRegistrations.filter(r =>
                                  !String(r.statusBayar || '').toLowerCase().includes('sudah')
                                );
                                const remainingSlots = slotCapacity - paidList.length;
                                const registeredUnpaid = unpaidList.filter((_, index) => index < remainingSlots);
                                const totalRegistered = paidList.length + registeredUnpaid.length;
                                const percentage = slotCapacity > 0 ? (totalRegistered / slotCapacity) * 100 : 0;
                                return Math.min(percentage, 100);
                              })()}
                              sx={{
                                height: { xs: 6, sm: 8 },
                                borderRadius: 4,
                                background: (theme) => theme.palette.mode === 'dark' 
                                  ? 'rgba(99,170,255,0.1)' 
                                  : 'rgba(25,118,210,0.1)',
                                '& .MuiLinearProgress-bar': {
                                  background: (theme) => theme.palette.mode === 'dark' 
                                    ? 'linear-gradient(90deg, #63aaff 0%, #1976d2 100%)' 
                                    : 'linear-gradient(90deg, #1976d2 0%, #1565c0 100%)',
                                  borderRadius: 4
                                }
                              }}
                            />
                          </CardContent>
                        </Card>
                      )}

                      {/* Tournament System Rules */}
                      {activeInternalTournament.description && (
                        <Card sx={{
                          mb: 3,
                          background: (theme) => theme.palette.mode === 'dark' 
                            ? 'rgba(99,170,255,0.05)' 
                            : 'rgba(25,118,210,0.03)',
                          border: (theme) => theme.palette.mode === 'dark' 
                            ? '1px solid rgba(99,170,255,0.2)' 
                            : '1px solid rgba(25,118,210,0.1)',
                          borderRadius: 2
                        }}>
                          <CardContent sx={{ p: { xs: 2, sm: 2.5 }, '&:last-child': { pb: { xs: 2, sm: 2.5 } } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 }, mb: 2 }}>
                              <SportsIcon sx={{ 
                                fontSize: { xs: 20, sm: 22 }, 
                                color: (theme) => theme.palette.mode === 'dark' ? '#63aaff' : '#1976d2' 
                              }} />
                              <Typography variant="subtitle1" fontWeight={700} sx={{ 
                                color: (theme) => theme.palette.mode === 'dark' ? '#63aaff' : '#1976d2',
                                fontSize: { xs: '0.95rem', sm: '1rem' }
                              }}>
                                SISTEM TURNAMEN
                              </Typography>
                            </Box>
                            <Box sx={{ 
                              pl: { xs: 3, sm: 4 },
                              '& .rule-item': {
                                mb: 1.5,
                                '&:last-child': { mb: 0 }
                              }
                            }}>
                              {(() => {
                                const description = activeInternalTournament.description;
                                const lines = description.split('\n').filter(line => line.trim());
                                
                                return lines.map((line, index) => {
                                  const isListItem = line.trim().startsWith('-') || line.trim().startsWith('•') || /^\d+\./.test(line.trim());
                                  const isHeading = line.includes(':') && !isListItem && line.length < 50;
                                  
                                  if (isHeading) {
                                    return (
                                      <Typography key={index} variant="body2" fontWeight={700} sx={{ 
                                        mt: 1.5, 
                                        mb: 0.5,
                                        color: (theme) => theme.palette.mode === 'dark' ? '#90caf9' : '#1565c0',
                                        fontSize: { xs: '0.85rem', sm: '0.9rem' }
                                      }}>
                                        {line}
                                      </Typography>
                                    );
                                  }
                                  
                                  if (isListItem) {
                                    return (
                                      <Box key={index} className="rule-item" sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                        <CheckIcon sx={{ 
                                          fontSize: { xs: 14, sm: 16 }, 
                                          color: (theme) => theme.palette.mode === 'dark' ? '#63aaff' : '#1976d2',
                                          mt: 0.2,
                                          flexShrink: 0
                                        }} />
                                        <Typography variant="body2" sx={{ lineHeight: 1.6, fontSize: { xs: '0.85rem', sm: '0.9rem' } }}>
                                          {line.replace(/^[-•]\s*|^\d+\.\s*/, '')}
                                        </Typography>
                                      </Box>
                                    );
                                  }
                                  
                                  return (
                                    <Typography key={index} variant="body2" sx={{ 
                                      lineHeight: 1.6,
                                      color: 'text.secondary',
                                      fontSize: { xs: '0.85rem', sm: '0.9rem' }
                                    }}>
                                      {line}
                                    </Typography>
                                  );
                                });
                              })()}
                            </Box>
                          </CardContent>
                        </Card>
                      )}

                      {/* paid registrations list (showing partner names/levels) */}
                      {(() => {
                        // Categorize registrations by status
                        const paidList = internalRegistrations.filter(r =>
                          String(r.statusBayar || '').toLowerCase().includes('sudah')
                        );
                        const unpaidList = internalRegistrations.filter(r =>
                          !String(r.statusBayar || '').toLowerCase().includes('sudah')
                        );
                        
                        // Sort by timestamp within each category, prioritize those with complete partners
                        const sortFn = (a: any, b: any) => {
                          // First, prioritize those with complete partners
                          const hasPartnerA = a.namaP2 && a.namaP2.trim() !== '';
                          const hasPartnerB = b.namaP2 && b.namaP2.trim() !== '';
                          
                          if (hasPartnerA && !hasPartnerB) return -1; // A has partner, B doesn't - A comes first
                          if (!hasPartnerA && hasPartnerB) return 1;  // B has partner, A doesn't - B comes first
                          
                          // If both have or both don't have partners, sort by timestamp
                          const timeA = new Date(a.timestamp || 0).getTime();
                          const timeB = new Date(b.timestamp || 0).getTime();
                          return timeA - timeB;
                        };
                        
                        paidList.sort(sortFn);
                        unpaidList.sort(sortFn);
                        
                        // Determine waiting list (unpaid players beyond remaining slot capacity)
                        const slotCapacity = parseInt(activeInternalTournament.slot || '0');
                        const remainingSlots = slotCapacity - paidList.length;
                        const registeredUnpaid = unpaidList.filter((_, index) => index < remainingSlots);
                        const waitingList = unpaidList.filter((_, index) => index >= remainingSlots);
                        
                        // Combine registered players (paid + registered unpaid)
                        const registeredPlayers = [...paidList, ...registeredUnpaid];
                        
                        if (registeredPlayers.length > 0 || waitingList.length > 0) {
                          return (
                            <>
                              {/* Registered Players Section */}
                              {registeredPlayers.length > 0 && (
                                <Card sx={{
                                  mb: 3,
                                  background: (theme) => theme.palette.mode === 'dark' 
                                    ? 'rgba(99,170,255,0.05)' 
                                    : 'rgba(25,118,210,0.03)',
                                  border: (theme) => theme.palette.mode === 'dark' 
                                    ? '1px solid rgba(99,170,255,0.2)' 
                                    : '1px solid rgba(25,118,210,0.1)',
                                  borderRadius: 2
                                }}>
                                  <CardContent sx={{ p: { xs: 2, sm: 2.5 }, '&:last-child': { pb: { xs: 2, sm: 2.5 } } }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 } }}>
                                        <CheckIcon sx={{ 
                                          fontSize: { xs: 20, sm: 22 }, 
                                          color: (theme) => theme.palette.mode === 'dark' ? '#63aaff' : '#1976d2' 
                                        }} />
                                        <Typography variant="subtitle1" fontWeight={700} sx={{ 
                                          color: (theme) => theme.palette.mode === 'dark' ? '#63aaff' : '#1976d2',
                                          fontSize: { xs: '0.95rem', sm: '1rem' }
                                        }}>
                                          PEMAIN TERDAFTAR
                                        </Typography>
                                      </Box>
                                      <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}>
                                        {registeredPlayers.length}/{activeInternalTournament.slot}
                                      </Typography>
                                    </Box>
                                    
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: { xs: '0.85rem', sm: '0.9rem' } }}>
                                      Ingin melihat detail level pemain? Kunjungi halaman{' '}
                                      <Link
                                        component="button"
                                        onClick={() => navigate('/leveling')}
                                        sx={{ 
                                          cursor: 'pointer', 
                                          fontWeight: 600, 
                                          p: 0, 
                                          border: 'none', 
                                          background: 'none',
                                          color: (theme) => theme.palette.mode === 'dark' ? '#63aaff' : '#1976d2'
                                        }}
                                      >
                                        Leveling
                                      </Link>
                                      {' '}untuk info lengkap.
                                    </Typography>

                                    <Grid container spacing={{ xs: 1, sm: 1.5 }}>
                                      {registeredPlayers.map((r) => {
                                        const getLevelColor = (lvl: string) => {
                                          if (!lvl) return 'default';
                                          const base = lvl.toLowerCase().replace(/[+\-\s]*$/, '');
                                          if (base.includes('advanced')) return 'error';
                                          if (base.includes('intermediate')) return 'warning';
                                          if (base.includes('beginner')) return 'success';
                                          return 'default';
                                        };

                                        const hasPartner = r.namaP2 && r.namaP2.trim() !== '';
                                        const isPaid = String(r.statusBayar || '').toLowerCase().includes('sudah');
                                        
                                        // Card styling based on status
                                        const getCardStyle = () => {
                                          if (isPaid) {
                                            return {
                                              background: (theme: any) => theme.palette.mode === 'dark' 
                                                ? 'rgba(76,175,80,0.08)' 
                                                : 'rgba(76,175,80,0.05)',
                                              border: (theme: any) => theme.palette.mode === 'dark' 
                                                ? '1px solid rgba(76,175,80,0.3)' 
                                                : '1px solid rgba(76,175,80,0.2)',
                                            };
                                          } else {
                                            return {
                                              background: (theme: any) => theme.palette.mode === 'dark' 
                                                ? 'rgba(255,152,0,0.08)' 
                                                : 'rgba(255,152,0,0.05)',
                                              border: (theme: any) => theme.palette.mode === 'dark' 
                                                ? '1px solid rgba(255,152,0,0.3)' 
                                                : '1px solid rgba(255,152,0,0.2)',
                                            };
                                          }
                                        };
                                        
                                        return (
                                          <Grid item xs={12} sm={6} md={4} key={r.id}>
                                            <Box sx={{
                                              p: { xs: 1.2, sm: 1.5 },
                                              borderRadius: 2,
                                              ...getCardStyle(),
                                              transition: 'all 0.2s ease',
                                              '&:hover': {
                                                background: (theme) => theme.palette.mode === 'dark' 
                                                  ? (isPaid ? 'rgba(76,175,80,0.12)' : 'rgba(255,152,0,0.12)')
                                                  : (isPaid ? 'rgba(76,175,80,0.08)' : 'rgba(255,152,0,0.08)'),
                                                transform: 'translateY(-2px)'
                                              }
                                            }}>
                                              {/* Status chip */}
                                              <Box sx={{ mb: { xs: 0.8, sm: 1 } }}>
                                                <Chip 
                                                  label={isPaid ? 'Sudah Bayar' : 'Belum Bayar'}
                                                  size="small"
                                                  sx={{ 
                                                    height: { xs: 18, sm: 20 }, 
                                                    fontSize: { xs: '0.6rem', sm: '0.65rem' },
                                                    fontWeight: 600,
                                                    background: (theme) => theme.palette.mode === 'dark' 
                                                      ? (isPaid ? 'rgba(76,175,80,0.2)' : 'rgba(255,152,0,0.2)')
                                                      : (isPaid ? 'rgba(76,175,80,0.1)' : 'rgba(255,152,0,0.1)'),
                                                    color: (theme) => theme.palette.mode === 'dark' 
                                                      ? (isPaid ? '#81c784' : '#ffb74d')
                                                      : (isPaid ? '#4caf50' : '#ff9800'),
                                                    border: (theme) => theme.palette.mode === 'dark' 
                                                      ? (isPaid ? '1px solid rgba(76,175,80,0.4)' : '1px solid rgba(255,152,0,0.4)')
                                                      : (isPaid ? '1px solid rgba(76,175,80,0.3)' : '1px solid rgba(255,152,0,0.3)'),
                                                  }}
                                                />
                                              </Box>
                                              
                                              {/* Horizontal layout with partnership separator */}
                                              <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.6, sm: 1 } }}>
                                                {/* Player 1 */}
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.6, sm: 0.8 }, flex: 1, minWidth: 0 }}>
                                                  <PersonIcon sx={{ 
                                                    fontSize: { xs: 16, sm: 18 }, 
                                                    color: (theme) => theme.palette.mode === 'dark' 
                                                      ? (isPaid ? '#81c784' : '#ffb74d')
                                                      : (isPaid ? '#4caf50' : '#ff9800'),
                                                    flexShrink: 0
                                                  }} />
                                                  <Box sx={{ minWidth: 0 }}>
                                                    <Typography variant="body2" fontWeight={600} noWrap sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>
                                                      {r.namaP1}
                                                    </Typography>
                                                    <Chip 
                                                      label={r.levelP1 || '?'}
                                                      size="small"
                                                      color={getLevelColor(r.levelP1)}
                                                      sx={{ 
                                                        height: { xs: 16, sm: 18 }, 
                                                        fontSize: { xs: '0.6rem', sm: '0.65rem' },
                                                        fontWeight: 600,
                                                        mt: { xs: 0.2, sm: 0.3 }
                                                      }}
                                                    />
                                                  </Box>
                                                </Box>
                                                
                                                {/* Partnership separator */}
                                                <Typography 
                                                  variant="caption" 
                                                  sx={{ 
                                                    color: (theme) => theme.palette.mode === 'dark' 
                                                      ? (isPaid ? '#81c784' : '#ffb74d')
                                                      : (isPaid ? '#4caf50' : '#ff9800'),
                                                    fontWeight: 700,
                                                    fontSize: { xs: '0.8rem', sm: '0.9rem' },
                                                    px: { xs: 0.3, sm: 0.5 },
                                                    flexShrink: 0
                                                  }}
                                                >
                                                  /
                                                </Typography>
                                                
                                                {/* Player 2 or Partner */}
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.6, sm: 0.8 }, flex: 1, minWidth: 0 }}>
                                                  <PersonIcon sx={{ 
                                                    fontSize: { xs: 16, sm: 18 }, 
                                                    color: (theme) => theme.palette.mode === 'dark' 
                                                      ? (isPaid ? '#81c784' : '#ffb74d')
                                                      : (isPaid ? '#4caf50' : '#ff9800'),
                                                    flexShrink: 0
                                                  }} />
                                                  <Box sx={{ minWidth: 0 }}>
                                                    <Typography variant="body2" fontWeight={600} noWrap sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>
                                                      {hasPartner ? r.namaP2 : 'Partner'}
                                                    </Typography>
                                                    <Chip 
                                                      label={hasPartner ? (r.levelP2 || '?') : '?'}
                                                      size="small"
                                                      color={hasPartner ? getLevelColor(r.levelP2) : 'default'}
                                                      sx={{ 
                                                        height: { xs: 16, sm: 18 }, 
                                                        fontSize: { xs: '0.6rem', sm: '0.65rem' },
                                                        fontWeight: 600,
                                                        mt: { xs: 0.2, sm: 0.3 }
                                                      }}
                                                    />
                                                  </Box>
                                                </Box>
                                              </Box>
                                            </Box>
                                          </Grid>
                                        );
                                      })}
                                    </Grid>
                                  </CardContent>
                                </Card>
                              )}

                              {/* Waiting List Section */}
                              {waitingList.length > 0 && (
                                <Card sx={{
                                  mb: 3,
                                  background: (theme) => theme.palette.mode === 'dark' 
                                    ? 'rgba(244,67,54,0.05)' 
                                    : 'rgba(244,67,54,0.03)',
                                  border: (theme) => theme.palette.mode === 'dark' 
                                    ? '1px solid rgba(244,67,54,0.2)' 
                                    : '1px solid rgba(244,67,54,0.1)',
                                  borderRadius: 2
                                }}>
                                  <CardContent sx={{ p: { xs: 2, sm: 2.5 }, '&:last-child': { pb: { xs: 2, sm: 2.5 } } }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 } }}>
                                        <PersonIcon sx={{ 
                                          fontSize: { xs: 20, sm: 22 }, 
                                          color: (theme) => theme.palette.mode === 'dark' ? '#e57373' : '#f44336' 
                                        }} />
                                        <Typography variant="subtitle1" fontWeight={700} sx={{ 
                                          color: (theme) => theme.palette.mode === 'dark' ? '#e57373' : '#f44336',
                                          fontSize: { xs: '0.95rem', sm: '1rem' }
                                        }}>
                                          WAITING LIST
                                        </Typography>
                                      </Box>
                                      <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}>
                                        {waitingList.length}
                                      </Typography>
                                    </Box>

                                    <Grid container spacing={{ xs: 1, sm: 1.5 }}>
                                      {waitingList.map((r) => {
                                        const getLevelColor = (lvl: string) => {
                                          if (!lvl) return 'default';
                                          const base = lvl.toLowerCase().replace(/[+\-\s]*$/, '');
                                          if (base.includes('advanced')) return 'error';
                                          if (base.includes('intermediate')) return 'warning';
                                          if (base.includes('beginner')) return 'success';
                                          return 'default';
                                        };

                                        const hasPartner = r.namaP2 && r.namaP2.trim() !== '';
                                        
                                        return (
                                          <Grid item xs={12} sm={6} md={4} key={r.id}>
                                            <Box sx={{
                                              p: { xs: 1.2, sm: 1.5 },
                                              borderRadius: 2,
                                              background: (theme) => theme.palette.mode === 'dark' 
                                                ? 'rgba(244,67,54,0.08)' 
                                                : 'rgba(244,67,54,0.05)',
                                              border: (theme) => theme.palette.mode === 'dark' 
                                                ? '1px solid rgba(244,67,54,0.3)' 
                                                : '1px solid rgba(244,67,54,0.2)',
                                              transition: 'all 0.2s ease',
                                              '&:hover': {
                                                background: (theme) => theme.palette.mode === 'dark' 
                                                  ? 'rgba(244,67,54,0.12)' 
                                                  : 'rgba(244,67,54,0.08)',
                                                transform: 'translateY(-2px)'
                                              }
                                            }}>
                                              {/* Status chip */}
                                              <Box sx={{ mb: { xs: 0.8, sm: 1 } }}>
                                                <Chip 
                                                  label="Belum Bayar"
                                                  size="small"
                                                  sx={{ 
                                                    height: { xs: 18, sm: 20 }, 
                                                    fontSize: { xs: '0.6rem', sm: '0.65rem' },
                                                    fontWeight: 600,
                                                    background: (theme) => theme.palette.mode === 'dark' 
                                                      ? 'rgba(255,152,0,0.2)' 
                                                      : 'rgba(255,152,0,0.1)',
                                                    color: (theme) => theme.palette.mode === 'dark' ? '#ffb74d' : '#ff9800',
                                                    border: (theme) => theme.palette.mode === 'dark' 
                                                      ? '1px solid rgba(255,152,0,0.4)' 
                                                      : '1px solid rgba(255,152,0,0.3)',
                                                  }}
                                                />
                                              </Box>
                                              
                                              {/* Horizontal layout with partnership separator */}
                                              <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.6, sm: 1 } }}>
                                                {/* Player 1 */}
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.6, sm: 0.8 }, flex: 1, minWidth: 0 }}>
                                                  <PersonIcon sx={{ 
                                                    fontSize: { xs: 16, sm: 18 }, 
                                                    color: (theme) => theme.palette.mode === 'dark' ? '#e57373' : '#f44336',
                                                    flexShrink: 0
                                                  }} />
                                                  <Box sx={{ minWidth: 0 }}>
                                                    <Typography variant="body2" fontWeight={600} noWrap sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>
                                                      {r.namaP1}
                                                    </Typography>
                                                    <Chip 
                                                      label={r.levelP1 || '?'}
                                                      size="small"
                                                      color={getLevelColor(r.levelP1)}
                                                      sx={{ 
                                                        height: { xs: 16, sm: 18 }, 
                                                        fontSize: { xs: '0.6rem', sm: '0.65rem' },
                                                        fontWeight: 600,
                                                        mt: { xs: 0.2, sm: 0.3 }
                                                      }}
                                                    />
                                                  </Box>
                                                </Box>
                                                
                                                {/* Partnership separator */}
                                                <Typography 
                                                  variant="caption" 
                                                  sx={{ 
                                                    color: (theme) => theme.palette.mode === 'dark' ? '#e57373' : '#f44336',
                                                    fontWeight: 700,
                                                    fontSize: { xs: '0.8rem', sm: '0.9rem' },
                                                    px: { xs: 0.3, sm: 0.5 },
                                                    flexShrink: 0
                                                  }}
                                                >
                                                  /
                                                </Typography>
                                                
                                                {/* Player 2 or Partner */}
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.6, sm: 0.8 }, flex: 1, minWidth: 0 }}>
                                                  <PersonIcon sx={{ 
                                                    fontSize: { xs: 16, sm: 18 }, 
                                                    color: (theme) => theme.palette.mode === 'dark' ? '#e57373' : '#f44336',
                                                    flexShrink: 0
                                                  }} />
                                                  <Box sx={{ minWidth: 0 }}>
                                                    <Typography variant="body2" fontWeight={600} noWrap sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>
                                                      {hasPartner ? r.namaP2 : 'Partner'}
                                                    </Typography>
                                                    <Chip 
                                                      label={hasPartner ? (r.levelP2 || '?') : '?'}
                                                      size="small"
                                                      color={hasPartner ? getLevelColor(r.levelP2) : 'default'}
                                                      sx={{ 
                                                        height: { xs: 16, sm: 18 }, 
                                                        fontSize: { xs: '0.6rem', sm: '0.65rem' },
                                                        fontWeight: 600,
                                                        mt: { xs: 0.2, sm: 0.3 }
                                                      }}
                                                    />
                                                  </Box>
                                                </Box>
                                              </Box>
                                            </Box>
                                          </Grid>
                                        );
                                      })}
                                    </Grid>
                                  </CardContent>
                                </Card>
                              )}
                            </>
                          );
                        } else {
                          return (
                            <Card sx={{
                              mb: 3,
                              background: (theme) => theme.palette.mode === 'dark' 
                                ? 'rgba(99,170,255,0.05)' 
                                : 'rgba(25,118,210,0.03)',
                              border: (theme) => theme.palette.mode === 'dark' 
                                ? '1px solid rgba(99,170,255,0.2)' 
                                : '1px solid rgba(25,118,210,0.1)',
                              borderRadius: 2
                            }}>
                              <CardContent sx={{ p: { xs: 2, sm: 2.5 }, '&:last-child': { pb: { xs: 2, sm: 2.5 } } }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 }, mb: 1 }}>
                                  <PersonIcon sx={{ 
                                    fontSize: { xs: 20, sm: 22 }, 
                                    color: (theme) => theme.palette.mode === 'dark' ? '#63aaff' : '#1976d2' 
                                  }} />
                                  <Typography variant="subtitle1" fontWeight={700} sx={{ 
                                    color: (theme) => theme.palette.mode === 'dark' ? '#63aaff' : '#1976d2',
                                    fontSize: { xs: '0.95rem', sm: '1rem' }
                                  }}>
                                    PEMAIN TERDAFTAR
                                  </Typography>
                                </Box>
                                <Typography variant="body2" color="text.secondary" sx={{ pl: { xs: 3, sm: 4 }, fontSize: { xs: '0.85rem', sm: '0.9rem' } }}>
                                  Belum ada pemain yang mendaftar. Jadilah yang pertama mendaftar!
                                </Typography>
                              </CardContent>
                            </Card>
                          );
                        }
                      })()}

                      {activeStatus.error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                          {activeStatus.error}
                        </Alert>
                      )}
                      {activeStatus.success && (
                        <Alert severity="success" sx={{ mb: 2 }}>
                          {activeStatus.success}
                        </Alert>
                      )}

                      {/* Registration CTA Button */}
                      <Box sx={{ display: 'flex', justifyContent: 'center', mt: { xs: 2, sm: 3 }, mb: 2 }}>
                        {isInternalTournamentFormLocked ? (
                          <Card sx={{
                            background: (theme) => theme.palette.mode === 'dark' 
                              ? 'rgba(99,170,255,0.08)' 
                              : 'rgba(25,118,210,0.05)',
                            border: (theme) => theme.palette.mode === 'dark' 
                              ? '1px solid rgba(99,170,255,0.3)' 
                              : '1px solid rgba(25,118,210,0.2)',
                            borderRadius: 3,
                            maxWidth: { xs: '100%', sm: 400 },
                            width: '100%'
                          }}>
                            <CardContent sx={{ p: { xs: 2, sm: 3 }, textAlign: 'center' }}>
                              <PersonIcon sx={{ 
                                fontSize: { xs: 40, sm: 48 }, 
                                color: (theme) => theme.palette.mode === 'dark' ? '#63aaff' : '#1976d2',
                                mb: 1
                              }} />
                              <Typography variant="body1" color="text.secondary" sx={{ mb: 2, fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                                Untuk mendaftar turnamen internal, silakan login terlebih dahulu.
                              </Typography>
                              <Button
                                variant="contained"
                                color="primary"
                                size="large"
                                startIcon={<PersonIcon />}
                                onClick={() => {
                                  try {
                                    localStorage.setItem('redirectAfterLogin', '/internal-tournament');
                                  } catch (e) {
                                    // ignore storage error
                                  }
                                  navigate('/login');
                                }}
                                sx={{
                                  background: (theme) => theme.palette.mode === 'dark' 
                                    ? 'linear-gradient(135deg, #63aaff 0%, #1976d2 100%)' 
                                    : 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                                  '&:hover': {
                                    background: (theme) => theme.palette.mode === 'dark' 
                                      ? 'linear-gradient(135deg, #42a5f5 0%, #1565c0 100%)' 
                                      : 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                                  }
                                }}
                              >
                                Login untuk Daftar
                              </Button>
                            </CardContent>
                          </Card>
                        ) : (
                          <Button
                            variant="contained"
                            size="large"
                            startIcon={<RegisterIcon />}
                            onClick={() => setRegistrationDialogOpen(true)}
                            sx={{
                              background: (theme) => theme.palette.mode === 'dark' 
                                ? 'linear-gradient(135deg, #63aaff 0%, #1976d2 100%)' 
                                : 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                              py: { xs: 1.5, sm: 2 },
                              px: { xs: 4, sm: 6 },
                              fontSize: { xs: '1rem', sm: '1.1rem' },
                              fontWeight: 700,
                              borderRadius: 3,
                              boxShadow: (theme) => theme.palette.mode === 'dark' 
                                ? '0 8px 24px rgba(99,170,255,0.4)' 
                                : '0 8px 24px rgba(25,118,210,0.3)',
                              '&:hover': {
                                background: (theme) => theme.palette.mode === 'dark' 
                                  ? 'linear-gradient(135deg, #42a5f5 0%, #1565c0 100%)' 
                                  : 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                                transform: 'translateY(-2px)',
                                boxShadow: (theme) => theme.palette.mode === 'dark' 
                                  ? '0 12px 32px rgba(99,170,255,0.5)' 
                                  : '0 12px 32px rgba(25,118,210,0.4)',
                              },
                              transition: 'all 0.3s ease'
                            }}
                          >
                            Daftar Turnamen Sekarang
                          </Button>
                        )}
                      </Box>
                    </Box>

                  </>
                )}
              </>
            )}
          </Box>
        )}

        {/* Tournament Standings & Matches Section */}
        {showOnlyTournament && (
          <Box sx={{ mt: 4 }}>
            <Typography variant="h5" fontWeight={700} align="center" gutterBottom sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
              <TrophyIcon sx={{ color: '#1976d2' }} />
              Klasemen & Pertandingan Turnamen
            </Typography>
            
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
              <Tabs value={tournamentTab} onChange={(e, v) => setTournamentTab(v)} centered>
                <Tab label="Klasemen Grup" />
                <Tab label="Babak Gugur" />
              </Tabs>
            </Box>

            {tournamentLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            )}

            {tournamentError && (
              <Alert severity="error" sx={{ mb: 3 }}>{tournamentError}</Alert>
            )}

            {!tournamentLoading && !tournamentError && (
              <>
                {/* Klasemen Grup Tab */}
                {tournamentTab === 0 && (
                  <Box>
                    {(() => {
                      // Group data by group letter
                      const groupedData = tournamentStandings.reduce((acc: any, team: any) => {
                        const group = team.Grup;
                        if (!acc[group]) acc[group] = [];
                        acc[group].push(team);
                        return acc;
                      }, {});

                      // Sort teams within each group by points (calculated from wins), then goal difference
                      Object.keys(groupedData).forEach((group: string) => {
                        groupedData[group].sort((a: any, b: any) => {
                          const pointsA = a.Menang * 3;
                          const pointsB = b.Menang * 3;
                          if (pointsB !== pointsA) return pointsB - pointsA;
                          return b['Selisih Poin'] - a['Selisih Poin'];
                        });
                      });

                      return Object.keys(groupedData).length === 0 ? (
                        <Alert severity="info">Belum ada data klasemen</Alert>
                      ) : (
                        <Grid container spacing={3}>
                          {Object.keys(groupedData).sort().map((group) => (
                            <Grid item xs={12} md={6} lg={6} key={group}>
                              <Card
                                sx={{
                                  background: theme.palette.mode === 'dark'
                                    ? 'linear-gradient(135deg, rgba(13,71,161,0.08) 0%, rgba(21,101,192,0.04) 100%)'
                                    : '#ffffff',
                                  border: theme.palette.mode === 'dark'
                                    ? '1px solid rgba(99,170,255,0.22)'
                                    : '1px solid rgba(0,0,0,0.1)',
                                  borderRadius: 2,
                                  height: '100%'
                                }}
                              >
                                <CardContent sx={{ p: 2 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                    <TrophyIcon sx={{ color: '#1976d2', fontSize: 24 }} />
                                    <Typography variant="h6" fontWeight={700}>
                                      Grup {group}
                                    </Typography>
                                  </Box>
                                  <TableContainer component={Paper} elevation={0} sx={{ background: 'transparent' }}>
                                    <Table size="small">
                                      <TableHead>
                                        <TableRow>
                                          <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', px: 1 }}>Tim</TableCell>
                                          <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', px: 1, textAlign: 'center' }}>M</TableCell>
                                          <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', px: 1, textAlign: 'center' }}>W</TableCell>
                                          <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', px: 1, textAlign: 'center' }}>L</TableCell>
                                          <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', px: 1, textAlign: 'center' }}>PF</TableCell>
                                          <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', px: 1, textAlign: 'center' }}>PA</TableCell>
                                          <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', px: 1, textAlign: 'center' }}>GD</TableCell>
                                          <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', px: 1, textAlign: 'center' }}>Terakhir</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {groupedData[group].map((team: any, index: number) => (
                                          <TableRow
                                            key={team['ID Tim']}
                                            sx={{
                                              backgroundColor: index < 2
                                                ? theme.palette.mode === 'dark'
                                                  ? 'rgba(76,175,80,0.1)'
                                                  : 'rgba(76,175,80,0.05)'
                                                : 'transparent',
                                              '&:last-child td, &:last-child th': { border: 0 }
                                            }}
                                          >
                                            <TableCell sx={{ px: 1, fontSize: '0.8rem', fontWeight: 600 }}>
                                              {team['Nama Tim']}
                                            </TableCell>
                                            <TableCell sx={{ px: 1, textAlign: 'center', fontSize: '0.8rem' }}>
                                              {team.Main}
                                            </TableCell>
                                            <TableCell sx={{ px: 1, textAlign: 'center', fontSize: '0.8rem' }}>
                                              {team.Menang}
                                            </TableCell>
                                            <TableCell sx={{ px: 1, textAlign: 'center', fontSize: '0.8rem' }}>
                                              {team.Kalah}
                                            </TableCell>
                                            <TableCell sx={{ px: 1, textAlign: 'center', fontSize: '0.8rem' }}>
                                              {team['Poin Masuk']}
                                            </TableCell>
                                            <TableCell sx={{ px: 1, textAlign: 'center', fontSize: '0.8rem' }}>
                                              {team['Poin Kemasukan']}
                                            </TableCell>
                                            <TableCell sx={{ px: 1, textAlign: 'center', fontSize: '0.8rem' }}>
                                              {team['Selisih Poin'] > 0 ? `+${team['Selisih Poin']}` : team['Selisih Poin']}
                                            </TableCell>
                                            <TableCell sx={{ px: 1, textAlign: 'center', fontSize: '0.7rem', fontWeight: 700 }}>
                                              {(() => {
                                                // Find last 5 matches for this team from tournamentMatches
                                                const teamName = team['Nama Tim'];
                                                const teamMatches = tournamentMatches.filter((match: any) => 
                                                  match.Fase === 'Grup' && 
                                                  (match['Team A'] === teamName || match['Team B'] === teamName)
                                                );
                                                
                                                if (teamMatches.length === 0) return '-';
                                                
                                                // Get last 5 matches (assuming they're ordered chronologically)
                                                const lastMatches = teamMatches.slice(-5);
                                                
                                                // Determine results for each match with colors
                                                const results = lastMatches.map((match: any) => {
                                                  const isTeamA = match['Team A'] === teamName;
                                                  const teamScore = isTeamA ? match['Score A'] : match['Score B'];
                                                  
                                                  // Win: score 30 (green), Lose: score not 30 (red)
                                                  if (teamScore === 30) {
                                                    return <span style={{ color: '#4CAF50' }}>✓</span>;
                                                  } else {
                                                    return <span style={{ color: '#F44336' }}>✗</span>;
                                                  }
                                                });
                                                
                                                return <span>{results.map((result, i) => (
                                                  <React.Fragment key={i}>
                                                    {i > 0 && <span style={{ marginLeft: '2px' }} />}
                                                    {result}
                                                  </React.Fragment>
                                                ))}</span>;
                                              })()}
                                            </TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </TableContainer>
                                </CardContent>
                              </Card>
                            </Grid>
                          ))}
                        </Grid>
                      );
                    })()}
                  </Box>
                )}

                {/* Babak Gugur Tab */}
                {tournamentTab === 1 && (
                  <Box>
                    <KnockoutBracket knockoutTeams={tournamentKnockout} tournamentMatches={tournamentMatches} />
                  </Box>
                )}
              </>
            )}
          </Box>
        )}

        {/* Dialog Pendaftaran Turnamen Internal */}
        <Dialog
          open={registrationDialogOpen}
          onClose={() => setRegistrationDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              background: (theme) =>
                theme.palette.mode === 'dark'
                  ? '#0a1626'
                  : 'rgba(255,255,255,0.96)',
            }
          }}
        >
          <DialogTitle sx={{ pb: 1 }}>
            <Box sx={{ textAlign: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
                <RegisterIcon sx={{ 
                  fontSize: 28, 
                  color: (theme) => theme.palette.mode === 'dark' ? '#63aaff' : '#1976d2' 
                }} />
                <Typography variant="h6" fontWeight={800} sx={{ 
                  color: (theme) => theme.palette.mode === 'dark' ? '#63aaff' : '#1976d2'
                }}>
                  Pendaftaran Turnamen
                </Typography>
              </Box>
              {activeInternalTournament && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {activeInternalTournament.name} — {activeInternalTournament.date}
                </Typography>
              )}
            </Box>
          </DialogTitle>
          <DialogContent>
            {activeInternalTournament && (
              <Box
                component="form"
                onSubmit={async (e: React.FormEvent) => {
                  e.preventDefault();
                  if (isInternalTournamentFormLocked) {
                    setInternalStatuses(prev => ({
                      ...prev,
                      [activeInternalTournament.id]: {
                        submitting: false,
                        error: 'Silakan login terlebih dahulu untuk mendaftar.',
                        success: null,
                      },
                    }));
                    return;
                  }
                  setInternalStatuses(prev => ({
                    ...prev,
                    [activeInternalTournament.id]: {
                      submitting: true,
                      error: null,
                      success: null,
                    },
                  }));

                  const payloadTanggal = activeInternalTournament.date || '';
                  const payloadTempat = activeInternalTournament.location || '';

                  if (!payloadTanggal || !payloadTempat) {
                    setInternalStatuses(prev => ({
                      ...prev,
                      [activeInternalTournament.id]: {
                        submitting: false,
                        error: 'Data turnamen tidak valid.',
                        success: null,
                      },
                    }));
                    return;
                  }
                  if (!activeForm.namaP1.trim()) {
                    setInternalStatuses(prev => ({
                      ...prev,
                      [activeInternalTournament.id]: {
                        submitting: false,
                        error: 'Nama pemain wajib diisi.',
                        success: null,
                      },
                    }));
                    return;
                  }
                  if (!activeForm.levelP1) {
                    setInternalStatuses(prev => ({
                      ...prev,
                      [activeInternalTournament.id]: {
                        submitting: false,
                        error: 'Level pemain wajib dipilih.',
                        success: null,
                      },
                    }));
                    return;
                  }
                  if (!activeForm.email.trim()) {
                    setInternalStatuses(prev => ({
                      ...prev,
                      [activeInternalTournament.id]: {
                        submitting: false,
                        error: 'Email wajib diisi.',
                        success: null,
                      },
                    }));
                    return;
                  }
                  if (!activeForm.nomorHP.trim()) {
                    setInternalStatuses(prev => ({
                      ...prev,
                      [activeInternalTournament.id]: {
                        submitting: false,
                        error: 'Nomor HP wajib diisi.',
                        success: null,
                      },
                    }));
                    return;
                  }

                  try {
                    const response = await axios.post(API_ENDPOINTS.INTERNAL_TOURNAMENT_REGISTER, {
                      tanggal: payloadTanggal,
                      tempat: payloadTempat,
                      namaP1: activeForm.namaP1.trim(),
                      levelP1: activeForm.levelP1,
                      email: activeForm.email.trim(),
                      nomorHP: activeForm.nomorHP.trim(),
                    });

                    if (response.data && response.data.success) {
                      setInternalStatuses(prev => ({
                        ...prev,
                        [activeInternalTournament.id]: {
                          submitting: false,
                          error: null,
                          success: 'Pendaftaran berhasil dikirim. Silakan cek informasi lebih lanjut dari admin.',
                        },
                      }));
                      // Reset nama & level, pertahankan kontak
                      setInternalForms(prev => ({
                        ...prev,
                        [activeInternalTournament.id]: {
                          ...activeForm,
                          namaP1: '',
                          levelP1: '',
                        },
                      }));

                      // re-fetch registrations so paid list updates
                      fetchRegistrations();

                      // trigger midtrans payment using id returned and fee
                      if (response.data.id) {
                        startMidtransPayment(response.data.id);
                      }

                      // Close dialog on success
                      setRegistrationDialogOpen(false);
                    } else {
                      setInternalStatuses(prev => ({
                        ...prev,
                        [activeInternalTournament.id]: {
                          submitting: false,
                          error: response.data?.message || 'Gagal mengirim pendaftaran.',
                          success: null,
                        },
                      }));
                    }
                  } catch (err: any) {
                    const msg =
                      err?.response?.data?.message ||
                      err?.message ||
                      'Gagal mengirim pendaftaran.';
                    setInternalStatuses(prev => ({
                      ...prev,
                      [activeInternalTournament.id]: {
                        submitting: false,
                        error: msg,
                        success: null,
                      },
                    }));
                  } finally {
                    setInternalStatuses(prev => ({
                      ...prev,
                      [activeInternalTournament.id]: {
                        ...(prev[activeInternalTournament.id] || { submitting: false, error: null, success: null }),
                        submitting: false,
                      },
                    }));
                  }
                }}
                sx={{ mt: 1 }}
              >
                <Grid container spacing={2}>
                  {/* Tanggal & Tempat (read-only, dari konfigurasi) */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Tanggal"
                      fullWidth
                      value={activeInternalTournament.date || ''}
                      InputProps={{ readOnly: true }}
                      disabled
                      size="small"
                      margin="dense"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Tempat"
                      fullWidth
                      value={activeInternalTournament.location || ''}
                      InputProps={{ readOnly: true }}
                      disabled
                      size="small"
                      margin="dense"
                    />
                  </Grid>

                  {/* Pemain */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Nama Pemain"
                      fullWidth
                      value={activeForm.namaP1}
                      onChange={(e) =>
                        setInternalForms(prev => ({
                          ...prev,
                          [activeInternalTournament.id]: {
                            ...(prev[activeInternalTournament.id] || {
                              namaP1: '',
                              levelP1: '',
                              email: '',
                              nomorHP: '',
                            }),
                            namaP1: e.target.value,
                          },
                        }))
                      }
                      disabled={isInternalTournamentFormLocked}
                      size="small"
                      margin="dense"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Level Pemain"
                      select
                      fullWidth
                      value={activeForm.levelP1}
                      onChange={(e) =>
                        setInternalForms(prev => ({
                          ...prev,
                          [activeInternalTournament.id]: {
                            ...(prev[activeInternalTournament.id] || {
                              namaP1: '',
                              levelP1: '',
                              email: '',
                              nomorHP: '',
                            }),
                            levelP1: e.target.value,
                          },
                        }))
                      }
                      disabled={isInternalTournamentFormLocked}
                      size="small"
                      margin="dense"
                    >
                      {levelOptions.map((lvl) => (
                        <MenuItem key={lvl} value={lvl}>
                          {lvl}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  {/* Kontak */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Email"
                      type="email"
                      fullWidth
                      value={activeForm.email}
                      onChange={(e) =>
                        setInternalForms(prev => ({
                          ...prev,
                          [activeInternalTournament.id]: {
                            ...(prev[activeInternalTournament.id] || {
                              namaP1: '',
                              levelP1: '',
                              email: '',
                              nomorHP: '',
                            }),
                            email: e.target.value,
                          },
                        }))
                      }
                      InputProps={{ readOnly: true }}
                      disabled
                      size="small"
                      margin="dense"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Nomor HP (WhatsApp)"
                      fullWidth
                      value={activeForm.nomorHP}
                      onChange={(e) =>
                        setInternalForms(prev => ({
                          ...prev,
                          [activeInternalTournament.id]: {
                            ...(prev[activeInternalTournament.id] || {
                              namaP1: '',
                              levelP1: '',
                              email: '',
                              nomorHP: '',
                            }),
                            nomorHP: e.target.value,
                          },
                        }))
                      }
                      disabled={isInternalTournamentFormLocked}
                      size="small"
                      margin="dense"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    {activeStatus.error && (
                      <Alert severity="error" sx={{ mb: 2 }}>
                        {activeStatus.error}
                      </Alert>
                    )}
                    {activeStatus.success && (
                      <Alert severity="success" sx={{ mb: 2 }}>
                        {activeStatus.success}
                      </Alert>
                    )}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
                      <Button
                        variant="outlined"
                        onClick={() => setRegistrationDialogOpen(false)}
                        disabled={activeStatus.submitting}
                      >
                        Batal
                      </Button>
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={activeStatus.submitting || isInternalTournamentFormLocked}
                      >
                        {activeStatus.submitting ? 'Mengirim...' : 'Daftar'}
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            )}
          </DialogContent>
        </Dialog>


      </TabPanel>
      )}

      {/* Tab Ranked Match */}
      {!showOnlyTournament && !showOnlyLiga && (
      <TabPanel value={mainTab} index={1}>
        <Accordion 
          expanded={expandedAccordion === 'champions'} 
          onChange={handleAccordionChange('champions')}
          sx={{ mb: 3, borderRadius: 2, background: (theme) => theme.palette.mode === 'dark' ? 'rgba(99,170,255,0.08)' : '#f5f9ff', border: (theme) => theme.palette.mode === 'dark' ? '1px solid rgba(99,170,255,0.25)' : '1px solid #e3f2fd' }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              🏆 Juara {selectedSeasonChampion === 'season1' ? 'Season 1' : 'Season 2'} (Ganda Solo) per Level
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', mb: 2, gap: 1, width: '100%' }}>
              <FormControl size="small" sx={{ minWidth: 150, maxWidth: 180 }}>
                <InputLabel>Pilih Season</InputLabel>
                <Select
                  value={selectedSeasonChampion}
                  label="Pilih Season"
                  onChange={(e) => setSelectedSeasonChampion(e.target.value as 'season1'|'season2')}
                >
                  <MenuItem value="season2">Season 2</MenuItem>
                  <MenuItem value="season1">Season 1</MenuItem>
                </Select>
              </FormControl>
            </Box>
            {rankedSeason1Loading ? (
              <Typography align="center" variant="body2">Memuat data juara season 1...</Typography>
            ) : (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, width: '100%' }}>
                {['gabungan', 'beginner', 'intermediate', 'advanced'].map((lvl) => {
                  const labelMap: Record<string, string> = {
                    gabungan: 'Gabungan',
                    beginner: 'Beginner',
                    intermediate: 'Intermediate',
                    advanced: 'Advanced'
                  };
                  const championData = selectedSeasonChampion === 'season2' ? rankedSeason2Top3ByLevel : rankedSeason1Top3ByLevel;
                  const data = championData[lvl as keyof typeof championData] || [];
                  return (
                    <Paper key={lvl} sx={{ p: 2, background: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'white', borderRadius: 2, boxShadow: '0 6px 18px rgba(0,0,0,0.06)' }}>
                      <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 700 }}>{labelMap[lvl]}</Typography>
                      {data.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">Belum ada data.</Typography>
                      ) : (
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 1.5 }}>
                          {data.map((player: any, idx: number) => {
                            const rankColor = getRankColor(player.rank || '');
                            return (
                              <Paper key={player.nama || idx} sx={{ p: 1.5, textAlign: 'center', borderRadius: 2, background: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#fafafa' }}>
                                <Typography variant="subtitle2" color="text.secondary"># {idx + 1}</Typography>
                                <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 600 }}>{player.nama || 'Tanpa Nama'}</Typography>
                                <Typography variant="body2" color="text.secondary">Poin: {player.poin ?? '-'}</Typography>
                                {player.rank ? (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      display: 'inline-block',
                                      mt: 0.5,
                                      px: 1,
                                      py: 0.4,
                                      borderRadius: 999,
                                      background: `${rankColor}22`,
                                      color: rankColor,
                                      fontWeight: 700
                                    }}
                                  >
                                    {player.rank}
                                  </Typography>
                                ) : null}
                              </Paper>
                            );
                          })}
                        </Box>
                      )}
                    </Paper>
                  );
                })}
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
        <Box sx={{ mb: 4, display: 'none' }}>
          <Typography variant="h6" gutterBottom align="center">
            Statistik Ranked Match
          </Typography>
          
          {/* Wrapper Ranked Table */}
          <Box sx={{
            mt: 2,
            borderRadius: 3,
            background: (theme) => theme.palette.mode === 'dark' ? 'linear-gradient(135deg, rgba(13,71,161,0.08) 0%, rgba(21,101,192,0.04) 100%)' : undefined,
            border: (theme) => theme.palette.mode === 'dark' ? '1px solid rgba(99,170,255,0.22)' : undefined,
            overflow: 'hidden',
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '1px',
              background: 'linear-gradient(90deg, transparent, rgba(99,170,255,0.55), transparent)'
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: 0,
              left: 0,
              width: '100%',
              height: '1px',
              background: 'linear-gradient(90deg, transparent, rgba(99,170,255,0.55), transparent)'
            }
          }}>
            <Box sx={{ width: '100%', overflowX: 'auto' }}>
              <Table sx={{ 
                width: '100%',
                tableLayout: 'fixed',
                '& .MuiTableCell-root': {
                  px: { xs: 0.5, sm: 1, md: 2 },
                  py: { xs: 0.5, sm: 1, md: 1.5 },
                  fontSize: { xs: '0.7rem', sm: '0.875rem', md: '1rem' },
                  lineHeight: { xs: 1.1, sm: 1.2 },
                  textAlign: 'center'
                }
              }}>
                <TableHead sx={{
                  backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'transparent' : 'primary.main',
                  '& .MuiTableCell-root': {
                    color: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.9)' : 'white',
                    fontWeight: 700,
                    fontSize: { xs: '0.8rem', sm: '0.875rem', md: '1rem' },
                    px: { xs: 0.5, sm: 1, md: 2 },
                    py: { xs: 0.75, sm: 1, md: 1.5 },
                    lineHeight: { xs: 1.3, sm: 1.4 },
                    borderBottom: (theme) => theme.palette.mode === 'dark' ? '2px solid rgba(99,170,255,0.35)' : '2px solid',
                    borderBottomColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(99,170,255,0.35)' : 'primary.dark'
                  }
                }}>
                  <TableRow>
                    <TableCell sx={{ width: { xs: '10%', sm: '10%' }, minWidth: { xs: 50, sm: 50 }, textAlign: 'center' }}><span className="emoji">🏆</span> Rank</TableCell>
                    <TableCell sx={{ width: { xs: '18%', sm: '18%' }, minWidth: { xs: 85, sm: 95 }, maxWidth: { xs: 115, sm: 140 }, overflow: 'visible', textOverflow: 'unset', whiteSpace: 'normal', wordWrap: 'break-word', textAlign: 'center' }}><span className="emoji">👤</span> Nama</TableCell>
                    <TableCell sx={{ width: { xs: '18%', sm: '18%' }, minWidth: { xs: 100, sm: 110 }, textAlign: 'center' }}><span className="emoji">🏅</span> Rank Badge</TableCell>
                    <TableCell sx={{ 
                      width: { xs: '14%', sm: '14%' },
                      minWidth: { xs: 70, sm: 70 }
                    }}><span className="emoji">📈</span> Level</TableCell>
                    <TableCell sx={{ 
                      width: { xs: '14%', sm: '14%' },
                      minWidth: { xs: 70, sm: 70 }
                    }}><span className="emoji">🎯</span> Level Turnamen</TableCell>
                    <TableCell sx={{ 
                      width: { xs: '10%', sm: '12%' },
                      minWidth: { xs: 60, sm: 55 }
                    }}><span className="emoji">⭐</span> Poin</TableCell>
                    <TableCell sx={{ 
                      width: { xs: '16%', sm: '14%' },
                      minWidth: { xs: 70, sm: 55 }
                    }}><span className="emoji">📊</span> WR%</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rankedMatchLoading ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                          <CircularProgress size={50} thickness={4} color="primary" />
                          <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 600 }}>
                            Memuat data ranked match...
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Mohon tunggu sebentar
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : getRankedMatchData().length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                          {searchQuery.trim() ? (
                            <>
                              <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 600 }}>
                                🔍 Tidak ada hasil ditemukan
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Tidak ada pemain yang cocok dengan pencarian "{searchQuery}"
                              </Typography>
                              <Button
                                variant="outlined"
                                onClick={() => setSearchQuery('')}
                                startIcon={<CloseIcon />}
                                sx={{ mt: 1 }}
                              >
                                Hapus Pencarian
                              </Button>
                            </>
                          ) : (
                            <>
                              <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 600 }}>
                                📭 Belum ada data ranked match
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Kategori: {getCategoryLabel()}
                                {selectedLevel !== 'Gabungan' && ` - Level ${selectedLevel}`}
                              </Typography>
                            </>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    getRankedMatchData().map((player: any, idx: number) => (
                      <TableRow 
                        key={idx}
                        onClick={() => handlePlayerClick(player)}
                        sx={{
                          '&:nth-of-type(odd)': {
                            backgroundColor: 'action.hover',
                          },
                          '&:hover': {
                            backgroundColor: 'action.selected',
                            transform: 'scale(1.01)',
                            transition: 'all 0.2s ease-in-out',
                            cursor: 'pointer'
                          },
                          transition: 'all 0.2s ease-in-out',
                          cursor: 'pointer'
                        }}
                      >
                        <TableCell sx={{ textAlign: 'center', py: 2 }}>
                          <RankNumber position={player.levelRank} />
                        </TableCell>
                        <TableCell sx={{ overflow: 'visible', textOverflow: 'unset', whiteSpace: 'normal', wordWrap: 'break-word', maxWidth: 180, fontSize: { xs: '0.7rem', sm: '1rem' }, lineHeight: 1.3, textAlign: 'center', fontWeight: 700 }}>
                          {highlightSearchQuery(getMaskedName(player.nama, player.winRate), searchQuery)}
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center', verticalAlign: 'middle !important' }}>
                          <RankBadge rank={player.rank} />
                        </TableCell>
                        <TableCell sx={{
                          fontSize: { xs: '0.7rem', sm: '1rem' },
                          fontWeight: 600,
                          color: getLevelColor(player.level || ''),
                          textAlign: 'left'
                        }}>
                          {player.level || '-'}
                        </TableCell>
                        <TableCell sx={{
                          fontSize: { xs: '0.7rem', sm: '1rem' },
                          fontWeight: 600,
                          color: 'warning.main',
                          textAlign: 'left'
                        }}>
                          {player.levelTurnamen || '-'}
                        </TableCell>
                        <TableCell sx={{
                          fontSize: { xs: '0.7rem', sm: '1rem' },
                          fontWeight: 600,
                          color: 'primary.main'
                        }}>
                          {player.poin}
                        </TableCell>
                        <TableCell sx={{ 
                          fontSize: { xs: '0.7rem', sm: '1rem' },
                          fontWeight: 600,
                          color: player.winRate >= 71 ? 'success.main' : player.winRate >= 50 ? 'warning.main' : 'error.main'
                        }}>
                          {player.winRate}%
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Box>
          </Box>
        </Box>

        {/* Informasi Ranked Match RaketerS */}
        <Accordion 
          expanded={expandedAccordion === 'penilaian'} 
          onChange={handleAccordionChange('penilaian')}
          sx={{ 
            mb: 4,
            background: (theme) => theme.palette.mode === 'dark'
              ? 'linear-gradient(135deg, rgba(13,71,161,0.08) 0%, rgba(21,101,192,0.04) 100%)'
              : undefined,
            border: (theme) => theme.palette.mode === 'dark' 
              ? '1px solid rgba(99,170,255,0.22)'
              : undefined,
            borderRadius: 3,
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '1px',
              background: 'linear-gradient(90deg, transparent, rgba(99,170,255,0.55), transparent)'
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: 0,
              left: 0,
              width: '100%',
              height: '1px',
              background: 'linear-gradient(90deg, transparent, rgba(99,170,255,0.55), transparent)'
            },
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: 'success.main' }}>
              📖 Penjelasan Detail Sistem Penilaian Season 3
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 3 }}>
            <Box sx={{ width: '100%' }}>
              {/* Divider */}
              <Divider sx={{ my: 3 }} />

              {/* Detailed Elo Rating Explanation */}
              <Box sx={{ mt: 3 }}>
                <Alert severity="info" sx={{ mb: 3 }}>
                  <Typography variant="body2">
                    <strong>🎯 Season 3 Updates:</strong> Difficulty 0.8x-2.2x (vs 1.0x-2.5x), max loss -12 (vs -25), 
                    bonus enhanced (+12/+20/+6 vs +8/+15/+4), sqrt-based plateau untuk progression yang lebih smooth.
                    Target: 50:50 win rate = ~72 games ke Legend!
                  </Typography>
                </Alert>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: 'text.primary' }}>
                    📚 Bagaimana Sistem Bekerja?
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Season 3 menggunakan sistem <strong>Elo Rating</strong> yang lebih kompetitif namun tetap adil. Setiap pemain memiliki rating (poin) yang berubah berdasarkan hasil pertandingan dan selisih rating dengan lawan. Rating yang lebih tinggi berarti pemain lebih kuat.
                  </Typography>
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'text.primary' }}>
                    ⬆️ Mengapa Rating Berubah Berbeda-beda?
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Rating yang Anda dapatkan atau kehilangan tergantung pada beberapa faktor:
                  </Typography>
                  <Box sx={{ pl: 2, mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      <strong>1️⃣ Selisih Rating dengan Lawan:</strong> Semakin besar selisih rating, semakin besar potensi perubahan maksimal.
                      Rating sama (0 poin beda): max gain +30, max loss -15.
                      Selisih kecil (&lt;100): max gain +35, max loss -13.
                      Selisih sedang (100-200): max gain +42, max loss -10.
                      Selisih besar (&gt;200): max gain +50, max loss -8.
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      <strong>2️⃣ Tingkat Kesulitan Berdasarkan Rating Anda:</strong> Pemain dengan rating lebih tinggi mendapat multiplier kesulitan (0.8x hingga 2.2x).
                      Saat menang, poin yang didapat dibagi dengan sqrt(difficulty) untuk progression yang lebih smooth.
                      Saat kalah, penalty loss dikalikan dengan faktor tambahan (semakin mudah turun).
                      Ini menciptakan "rating plateau" yang mengharuskan konsistensi tinggi untuk mencapai level Legend.
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      <strong>3️⃣ Bonus Khusus (Season 3 Enhanced):</strong> Bonus yang lebih besar untuk semua level:
                      Winner bonus (+3-12 poin), underdog bonus untuk upset (+5-20 poin), dan participation bonus (+2-6 poin).
                      Pemain rating tinggi mendapat bonus yang lebih kecil karena kesulitan mereka sudah tinggi.
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'text.primary' }}>
                    💰 Contoh Perhitungan (Ganda - 2 vs 2)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Tim A: Rata-rata rating 207 (Beginner) | Tim B: Rata-rata rating 394 (Intermediate) → Tim B Menang
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Box sx={{
                        p: 2,
                        background: (theme) => theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.3)' : 'rgba(255,255,255,0.5)',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'divider'
                      }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 700 }}>
                          Tim A (Kalah) - Rating 207:
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          • Rating dasar: 207 (difficulty 0.8x - easier)</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          • Selisih 187 poin → max loss -8 (vs -10 sebelumnya)</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          • Participation bonus: +6 (vs +1 sebelumnya)</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 700, mt: 1 }}>
                          = -2 poin per pemain (vs -9 sebelumnya)</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box sx={{
                        p: 2,
                        background: (theme) => theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.3)' : 'rgba(255,255,255,0.5)',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'divider'
                      }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 700 }}>
                          Tim B (Menang) - Rating 394:
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          • Rating dasar: 394 (difficulty 1.0x-1.2x)</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          • Selisih 187 poin → max gain +42</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          • Winner bonus + sqrt plateau: +10-12 (vs +2-8 sebelumnya)</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 700, mt: 1 }}>
                          = +35-45 poin per pemain (vs +25-35 sebelumnya)</Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'text.primary' }}>
                    🛡️ Sistem Adil untuk Semua Level
                  </Typography>
                  <Box sx={{ pl: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      <strong>Pemain Baru (&lt;300):</strong> Difficulty 0.8x, max loss -12, bonus maksimal (+12/+20/+6). Dilindungi agar tidak putus asa.
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      <strong>Pemain Menengah (300-700):</strong> Difficulty 1.0x-1.2x, sistem seimbang dengan bonus moderat (+10-12/+17/+5).
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      <strong>Pemain Expert (700-900):</strong> Difficulty 1.2x-1.5x, bonus lebih kecil (+8/+13/+4), sqrt plateau effect mulai terasa.
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      <strong>Pemain Master (900-1000):</strong> Difficulty 1.5x-1.8x, sangat sulit naik (+7/+11/+3), konsistensi tinggi diperlukan.
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      <strong>Pemain Legend (1000+):</strong> Difficulty 1.8x-2.2x, ekstrim sulit naik (+5/+9/+3), hanya untuk pemain elite sejati.
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      <strong>Bonus Underdog (Enhanced):</strong> Jika pemain rating rendah menang melawan rating jauh lebih tinggi,
                      dapat bonus ekstra (+5-20 poin) karena upset victory yang mengesankan!
                    </Typography>
                  </Box>
                </Box>

                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'text.primary' }}>
                    🎮 Untuk Pertandingan Ganda (2 vs 2)
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Sistem menggunakan <strong>rata-rata rating tim</strong> untuk perhitungan. Semua 4 pemain mendapat <strong>perubahan rating yang sama</strong> berdasarkan hasil tim. Ini memastikan sistem fair dan mendorong teamwork!
                  </Typography>
                </Box>

                <Box sx={{ mt: 3, p: 2, backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,152,0,0.1)' : 'rgba(255,152,0,0.05)', borderRadius: 2, border: '1px solid', borderColor: 'warning.main' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'warning.main' }}>
                    ⚠️ Penalti Inaktivitas untuk Top Rank (Rank 1-5)
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Di akhir season, pemain yang berada di <strong>rank 1-5</strong> namun <strong>tidak mengikuti ranked match</strong> akan dikenakan <strong>penalti pengurangan poin</strong>. 
                    Adapun jumlah poin yang akan dikurangi dan kapan penalti ini akan diberlakukan akan diumumkan kemudian.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                    Tujuan: Mendorong konsistensi pemain top rank untuk tetap aktif berkompetisi dan mempertahankan posisi mereka.
                  </Typography>
                </Box>

                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" fontWeight="bold" sx={{ mb: 1, color: 'success.main' }}>
                    💪 Siapkah kamu menjadi Top Rank RaketerS?
                  </Typography>
                </Box>
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>
        
        {/* Informasi Tingkatan Rank */}
        <Accordion 
          expanded={expandedAccordion === 'tingkatan'} 
          onChange={handleAccordionChange('tingkatan')}
          sx={{ 
            mb: 4,
            background: (theme) => theme.palette.mode === 'dark'
              ? 'linear-gradient(135deg, rgba(13,71,161,0.08) 0%, rgba(21,101,192,0.04) 100%)'
              : undefined,
            border: (theme) => theme.palette.mode === 'dark' 
              ? '1px solid rgba(99,170,255,0.22)'
              : undefined,
            borderRadius: 3,
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '1px',
              background: 'linear-gradient(90deg, transparent, rgba(99,170,255,0.55), transparent)'
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: 0,
              left: 0,
              width: '100%',
              height: '1px',
              background: 'linear-gradient(90deg, transparent, rgba(99,170,255,0.55), transparent)'
            },
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Tingkatan Rank
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 3 }}>
            <Grid container spacing={2} sx={{ width: '100%' }}>
              {rankLevels.map((rank, index) => (
                <Grid item xs={12} sm={6} md={4} key={rank.name}>
                  <Card sx={{ 
                    border: `2px solid ${rank.color}`,
                    backgroundColor: `${rank.color}10`,
                    borderRadius: 3,
                    '&:hover': {
                      boxShadow: '0 12px 32px rgba(99,170,255,0.25)',
                      transform: 'translateY(-2px)',
                      transition: 'all 0.25s ease'
                    }
                  }}>
                    <CardContent sx={{ textAlign: 'center', py: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                        <img 
                          src={rank.icon} 
                          alt={rank.name} 
                          style={{ width: 60, height: 60 }}
                        />
                      </Box>
                      <Chip
                        label={rank.name}
                        size="small"
                        sx={{
                          backgroundColor: rank.color,
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.85rem' },
                          height: { xs: 24, sm: 28, md: 30 },
                          minWidth: 'fit-content',
                          maxWidth: 'none',
                          boxShadow: { xs: 1, sm: 2 },
                          mb: 1,
                          '& .MuiChip-label': {
                            overflow: 'visible',
                            textOverflow: 'unset',
                            whiteSpace: 'nowrap',
                            maxWidth: 'none',
                            px: { xs: 0.75, sm: 1, md: 1.25 }
                          }
                        }}
                      />
                      <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                        {rank.description}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        Min. {rank.minPoints} poin
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </AccordionDetails>
        </Accordion>


                         {/* Reset Season Button - Superadmin Only */}
         {isSuperAdmin ? (
           <Paper sx={{ p: 3, mb: 4,
             background: (theme) => theme.palette.mode === 'dark'
               ? 'linear-gradient(135deg, rgba(255,87,34,0.08) 0%, rgba(255,152,0,0.04) 100%)'
               : undefined,
             border: (theme) => theme.palette.mode === 'dark' 
               ? '1px solid rgba(255,87,34,0.22)'
               : '1px solid rgba(255,87,34,0.3)',
             borderRadius: 3
           }}>
             <Typography variant="h6" gutterBottom sx={{ color: 'warning.main', fontWeight: 700 }}>
               ⚠️ Reset Season Ranked Match
             </Typography>
             <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
               Reset poin semua pemain di ranked_double_solo sesuai aturan season baru
             </Typography>
             <Button
               variant="contained"
               color="warning"
               onClick={() => setResetSeasonDialogOpen(true)}
               fullWidth
               size="large"
               sx={{ 
                 py: 1.5, 
                 fontSize: '1rem',
                 fontWeight: 600,
                 borderRadius: 2
               }}
             >
               🔄 Reset Season
             </Button>
           </Paper>
         ) : null}

                         {/* Tombol ke Halaman Update Ranked Matches - Superadmin Only */}
         {isSuperAdmin ? (
           <Paper sx={{ p: 3, mb: 4,
             background: (theme) => theme.palette.mode === 'dark'
               ? 'linear-gradient(135deg, rgba(13,71,161,0.08) 0%, rgba(21,101,192,0.04) 100%)'
               : undefined,
             border: (theme) => theme.palette.mode === 'dark'
               ? '1px solid rgba(99,170,255,0.22)'
               : undefined,
             borderRadius: 3,
             position: 'relative',
             '&::before': {
               content: '""',
               position: 'absolute',
               top: 0,
               left: 0,
               width: '100%',
               height: '1px',
               background: 'linear-gradient(90deg, transparent, rgba(99,170,255,0.55), transparent)'
             },
             '&::after': {
               content: '""',
               position: 'absolute',
               bottom: 0,
               left: 0,
               width: '100%',
               height: '1px',
               background: 'linear-gradient(90deg, transparent, rgba(99,170,255,0.55), transparent)'
             },
           }}>
             <Typography variant="h6" gutterBottom>
               Update Win-Loss Ranked Match (Superadmin Only)
             </Typography>
             <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
               Admin: {adminEmail} | Kelola hasil pertandingan ranked match dengan sistem Elo rating
             </Typography>

             <Button
               variant="contained"
               color="primary"
               onClick={() => navigate('/update-ranked-matches')}
               fullWidth
               size="large"
               sx={{
                 py: 2,
                 fontSize: '1.1rem',
                 fontWeight: 600,
                 borderRadius: 2,
                 background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                 '&:hover': {
                   background: 'linear-gradient(45deg, #1976D2 30%, #1CB5E0 90%)',
                 }
               }}
             >
               🏆 Kelola Hasil Pertandingan
             </Button>
           </Paper>
         ) : null}

         {/* Statistik Ranked Match */}
         <Box sx={{ mb: 2 }}>
           <Typography variant="h6" gutterBottom align="center">
             Statistik Ranked Match
           </Typography>
           {/* Kontrol: Filter, Pencarian, dan Share */}
           <Box sx={{
             display: 'flex',
             justifyContent: 'space-between',
             alignItems: 'center',
             gap: { xs: 1, sm: 2 },
             flexWrap: 'wrap',
             mb: 1.5,
             px: { xs: 0.5, sm: 0 }
           }}>
             <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, flexWrap: 'wrap' }}>
               <FormControl sx={{ minWidth: { xs: 150, sm: 180, md: 200 } }} size="small">
                 <InputLabel>Pilih Kategori</InputLabel>
                 <Select
                   value={selectedCategory}
                   label="Pilih Kategori"
                   onChange={(e) => setSelectedCategory(e.target.value)}
                 >
                   <MenuItem value="tunggal">🏸 Tunggal</MenuItem>
                   <MenuItem value="ganda_solo">👥 Ganda (Solo)</MenuItem>
                   <MenuItem value="ganda_party">🎉 Ganda (Party)</MenuItem>
                 </Select>
               </FormControl>
               <FormControl sx={{ minWidth: { xs: 150, sm: 180, md: 200 } }} size="small">
                 <InputLabel>Pilih Level</InputLabel>
                 <Select
                   value={selectedLevel}
                   label="Pilih Level"
                   onChange={(e) => setSelectedLevel(e.target.value)}
                 >
                   <MenuItem value="Gabungan">All Levels</MenuItem>
                   <MenuItem value="Pemula">Beginner</MenuItem>
                   <MenuItem value="Menengah">Intermediate</MenuItem>
                   <MenuItem value="Lanjutan">Advanced</MenuItem>
                 </Select>
               </FormControl>
               <TextField
                 size="small"
                 placeholder="Cari pemain..."
                 value={searchQuery}
                 onChange={(e) => setSearchQuery(e.target.value)}
                 sx={{ minWidth: { xs: 180, sm: 220 } }}
               />
             </Box>
             <Button variant="contained" startIcon={<ShareIcon />} onClick={handleShareClick} sx={{ fontWeight: 600 }}>
               Share Leaderboard
             </Button>
           </Box>
           {/* Ranked Match: conditional mobile card vs desktop table */}
           {isMobileView ? (
             <Box ref={cardViewRef as any} sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 2 }}>
               {rankedMatchLoading ? (
                 <Paper sx={{ p: 2, borderRadius: 2, textAlign: 'center' }}>
                   <CircularProgress size={36} />
                 </Paper>
               ) : getRankedMatchData().length === 0 ? (
                 <Paper sx={{ p: 2, borderRadius: 2, textAlign: 'center' }}>
                   <Typography variant="body2" color="text.secondary">Belum ada data ranked match</Typography>
                 </Paper>
               ) : (
                 getRankedMatchData().map((player: any, idx: number) => (
                   <Paper key={idx} sx={{ p: 2, borderRadius: 2,
                     background: (theme) => theme.palette.mode === 'dark' ? '#0a1626' : '#fff',
                     border: (theme) => theme.palette.mode === 'dark' ? '1px solid rgba(99,170,255,0.22)' : '1px solid rgba(0,0,0,0.08)'
                   }} onClick={() => handlePlayerClick(player)}>
                     <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                       <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                         <RankNumber position={player.levelRank} />
                         <Typography sx={{ fontWeight: 700 }}>
                           {getMaskedName(player.nama, player.winRate)}
                         </Typography>
                       </Box>
                       <Typography sx={{ fontWeight: 700, color: 'primary.main' }}>{player.poin} pts</Typography>
                     </Box>
                     <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
                       <Box sx={{ textAlign: 'center' }}>
                         <Typography variant="caption" color="text.secondary">Badge</Typography>
                         <Box sx={{ mt: 0.5 }}>
                           <RankBadge rank={player.rank} />
                         </Box>
                       </Box>
                       <Box sx={{ textAlign: 'center' }}>
                         <Typography variant="caption" color="text.secondary">Level</Typography>
                         <Typography sx={{ fontWeight: 700, mt: 0.5, color: getLevelColor(player.level || '') }}>{player.level || '-'}</Typography>
                       </Box>
                       <Box sx={{ textAlign: 'center' }}>
                         <Typography variant="caption" color="text.secondary">WR%</Typography>
                         <Typography sx={{ fontWeight: 700, mt: 0.5, color: player.winRate >= 71 ? 'success.main' : player.winRate >= 50 ? 'warning.main' : 'error.main' }}>{player.winRate}%</Typography>
                       </Box>
                     </Box>
                   </Paper>
                 ))
               )}
             </Box>
           ) : (
            <Box id="ranked-leaderboard-wrapper" ref={tableRef as any} sx={{
              mt: 2,
              borderRadius: 3,
              background: (theme) => theme.palette.mode === 'dark' ? 'linear-gradient(135deg, rgba(13,71,161,0.08) 0%, rgba(21,101,192,0.04) 100%)' : undefined,
              border: (theme) => theme.palette.mode === 'dark' ? '1px solid rgba(99,170,255,0.22)' : undefined,
              overflow: 'hidden',
              position: 'relative',
              '&::before': { content: '""', position: 'absolute', top: 0, left: 0, width: '100%', height: '1px', background: 'linear-gradient(90deg, transparent, rgba(99,170,255,0.55), transparent)' },
              '&::after': { content: '""', position: 'absolute', bottom: 0, left: 0, width: '100%', height: '1px', background: 'linear-gradient(90deg, transparent, rgba(99,170,255,0.55), transparent)' }
            }}>
               <Box sx={{ width: '100%', overflowX: 'auto' }}>
                 <Table sx={{ 
                   width: '100%',
                   tableLayout: 'fixed',
                   '& .MuiTableCell-root': {
                     px: { xs: 0.5, sm: 1, md: 2 },
                     py: { xs: 0.5, sm: 1, md: 1.5 },
                     fontSize: { xs: '0.7rem', sm: '0.875rem', md: '1rem' },
                     lineHeight: { xs: 1.1, sm: 1.2 },
                     textAlign: 'center' // Centered all table cells
                   }
                 }}>
                   <TableHead sx={{
                     backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'transparent' : 'primary.main',
                     '& .MuiTableCell-root': {
                       color: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.9)' : 'white',
                       fontWeight: 700,
                       fontSize: { xs: '0.8rem', sm: '0.875rem', md: '1rem' },
                       px: { xs: 0.5, sm: 1, md: 2 },
                       py: { xs: 0.75, sm: 1, md: 1.5 },
                       lineHeight: { xs: 1.3, sm: 1.4 },
                       borderBottom: (theme) => theme.palette.mode === 'dark' ? '2px solid rgba(99,170,255,0.35)' : '2px solid',
                       borderBottomColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(99,170,255,0.35)' : 'primary.dark'
                     }
                   }}>
                     <TableRow>
                       <TableCell sx={{ width: { xs: '10%', sm: '10%' }, minWidth: { xs: 50, sm: 50 }, textAlign: 'center' }}><span className="emoji">🏆</span> Rank</TableCell>
                       <TableCell sx={{ width: { xs: '18%', sm: '18%' }, minWidth: { xs: 85, sm: 95 }, maxWidth: { xs: 115, sm: 140 }, overflow: 'visible', textOverflow: 'unset', whiteSpace: 'normal', wordWrap: 'break-word', textAlign: 'center' }}><span className="emoji">👤</span> Nama</TableCell>
                       <TableCell sx={{ width: { xs: '18%', sm: '18%' }, minWidth: { xs: 100, sm: 110 }, textAlign: 'center' }}><span className="emoji">🏅</span> Rank Badge</TableCell>
                       <TableCell sx={{
                         width: { xs: '14%', sm: '14%' },
                         minWidth: { xs: 70, sm: 70 }
                       }}><span className="emoji">📈</span> Level</TableCell>
                       <TableCell sx={{
                         width: { xs: '14%', sm: '14%' },
                         minWidth: { xs: 70, sm: 70 }
                       }}><span className="emoji">🎯</span> Level Turnamen</TableCell>
                       <TableCell sx={{
                         width: { xs: '10%', sm: '12%' },
                         minWidth: { xs: 60, sm: 55 }
                       }}><span className="emoji">⭐</span> Poin</TableCell>
                       <TableCell sx={{
                         width: { xs: '16%', sm: '14%' },
                         minWidth: { xs: 70, sm: 55 }
                       }}><span className="emoji">📊</span> WR%</TableCell>
                     </TableRow>
                   </TableHead>
                   <TableBody>
                     {rankedMatchLoading ? (
                       <TableRow>
                         <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                           <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                             <CircularProgress size={50} thickness={4} color="primary" />
                             <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 600 }}>
                               Memuat data ranked match...
                             </Typography>
                             <Typography variant="body2" color="text.secondary">
                               Mohon tunggu sebentar
                             </Typography>
                           </Box>
                         </TableCell>
                       </TableRow>
                     ) : getRankedMatchData().length === 0 ? (
                       <TableRow>
                         <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                           <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                             {searchQuery.trim() ? (
                               <>
                                 <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 600 }}>
                                   🔍 Tidak ada hasil ditemukan
                                 </Typography>
                                 <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                   Tidak ada pemain yang cocok dengan pencarian "{searchQuery}"
                                 </Typography>
                                 <Button variant="outlined" onClick={() => setSearchQuery('')} startIcon={<CloseIcon />} sx={{ mt: 1 }}>
                                   Hapus Pencarian
                                 </Button>
                               </>
                             ) : (
                               <>
                                 <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 600 }}>
                                   📭 Belum ada data ranked match
                                 </Typography>
                                 <Typography variant="body2" color="text.secondary">
                                   Kategori: {getCategoryLabel()}
                                   {selectedLevel !== 'Gabungan' && ` - Level ${selectedLevel}`}
                                 </Typography>
                               </>
                             )}
                           </Box>
                         </TableCell>
                       </TableRow>
                     ) : (
                       getRankedMatchData().map((player: any, idx: number) => (
                         <TableRow key={idx} onClick={() => handlePlayerClick(player)} sx={{
                           '&:nth-of-type(odd)': { backgroundColor: 'action.hover' },
                           '&:hover': { backgroundColor: 'action.selected', transform: 'scale(1.01)', transition: 'all 0.2s ease-in-out', cursor: 'pointer' },
                           transition: 'all 0.2s ease-in-out', cursor: 'pointer'
                         }}>
                           <TableCell sx={{ textAlign: 'center', py: 2 }}>
                             <RankNumber position={player.levelRank} />
                           </TableCell>
                           <TableCell sx={{ overflow: 'visible', textOverflow: 'unset', whiteSpace: 'normal', wordWrap: 'break-word', maxWidth: 180, fontSize: { xs: '0.7rem', sm: '1rem' }, lineHeight: 1.3, textAlign: 'center', fontWeight: 700 }}>
                             {highlightSearchQuery(getMaskedName(player.nama, player.winRate), searchQuery)}
                           </TableCell>
                           <TableCell sx={{ textAlign: 'center', verticalAlign: 'middle !important' }}>
                             <RankBadge rank={player.rank} />
                           </TableCell>
                           <TableCell sx={{
                             fontSize: { xs: '0.7rem', sm: '1rem' },
                             fontWeight: 600,
                             color: getLevelColor(player.level || ''),
                             textAlign: 'left'
                           }}>
                             {player.level || '-'}
                           </TableCell>
                           <TableCell sx={{
                             fontSize: { xs: '0.7rem', sm: '1rem' },
                             fontWeight: 600,
                             color: 'warning.main',
                             textAlign: 'left'
                           }}>
                             {player.levelTurnamen || '-'}
                           </TableCell>
                           <TableCell sx={{
                             fontSize: { xs: '0.7rem', sm: '1rem' },
                             fontWeight: 600,
                             color: 'primary.main'
                           }}>
                             {player.poin}
                           </TableCell>
                           <TableCell sx={{ 
                             fontSize: { xs: '0.7rem', sm: '1rem' },
                             fontWeight: 600,
                             color: player.winRate >= 71 ? 'success.main' : player.winRate >= 50 ? 'warning.main' : 'error.main'
                           }}>
                             {player.winRate}%
                           </TableCell>
                         </TableRow>
                       ))
                     )}
                   </TableBody>
                 </Table>
               </Box>
             </Box>
           )}
        </Box>

      </TabPanel>
      )}

      {/* Tab Liga */}
      {((!showOnlyRanked && !showOnlyTournament) || showOnlyLiga) && (
      <TabPanel value={mainTab} index={2}>
      <Box sx={{ mb: 4,
          position: 'relative',
          '&::before': { content: '""', position: 'absolute', top: 0, left: 0, width: '100%', height: '1px', background: 'linear-gradient(90deg, transparent, rgba(99,170,255,0.55), transparent)' },
          '&::after': { content: '""', position: 'absolute', bottom: 0, left: 0, width: '100%', height: '1px', background: 'linear-gradient(90deg, transparent, rgba(99,170,255,0.55), transparent)' }
        }}>
          <Typography variant="h6" gutterBottom align="center">
            Klasemen Liga
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="liga-location-label">Lokasi</InputLabel>
              <Select
                labelId="liga-location-label"
                value={ligaLocation}
                label="Lokasi"
                onChange={(e) => setLigaLocation(e.target.value)}
              >
                <MenuItem value="Liga Corona">Liga Corona</MenuItem>
<MenuItem value="Liga AGS">Liga AGS</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {ligaLoading && (
            <Typography align="center" sx={{ mt: 3 }}>Memuat klasemen...</Typography>
          )}
          {ligaError && (
            <Typography align="center" color="error" sx={{ mt: 3 }}>{ligaError}</Typography>
          )}
          {!ligaLoading && !ligaError && (
            <Box sx={{ mt: 3 }}>
              {/* Mobile Card View (xs) */}
              <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 2 }}>
                {ligaData.length === 0 ? (
                  <Paper sx={{ p: 2, borderRadius: 3,
                    background: (theme) => theme.palette.mode === 'dark' ? '#0a1626' : undefined,
                    border: (theme) => theme.palette.mode === 'dark' ? '1px solid rgba(99,170,255,0.22)' : undefined
                  }}>
                    <Typography variant="body2" color="text.secondary" align="center">
                      Belum ada data klasemen
                    </Typography>
                  </Paper>
                ) : (
                  getLigaComputedData().map((row, idx) => {
                    const lastTenArray = (row.last10 || '').split(',');
                    return (
                      <Paper key={`${row.nama}-${idx}`} sx={{ p: 2, borderRadius: 3,
                        background: (theme) => theme.palette.mode === 'dark' ? '#0a1626' : undefined,
                        border: (theme) => theme.palette.mode === 'dark' ? '1px solid rgba(99,170,255,0.22)' : undefined
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                            <Chip label={String(row.computedRank)} size="small" color="primary" sx={{ fontWeight: 700 }} />
                            <Typography sx={{ fontWeight: 700 }}>{row.nama}</Typography>
                          </Box>
                          <Chip label={`${row.poin} pts`} color="primary" size="small" sx={{ fontWeight: 700 }} />
                        </Box>

                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1.25, mb: 1.25 }}>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Main</Typography>
                            <Typography sx={{ fontWeight: 700 }}>{row.jumlahMain}</Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Menang</Typography>
                            <Typography sx={{ fontWeight: 700, color: 'success.main' }}>{row.menang}</Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Kalah</Typography>
                            <Typography sx={{ fontWeight: 700, color: 'error.main' }}>{row.kalah}</Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Seri</Typography>
                            <Typography sx={{ fontWeight: 700, color: 'warning.main' }}>{row.seri}</Typography>
                          </Box>
                        </Box>

                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>10 Match Terakhir</Typography>
                          {/* Bagi 10 terakhir jadi 2 baris: 5 atas & 5 bawah */}
                          <Box>
                            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 0.5, justifyContent: 'flex-start' }}>
                              {lastTenArray.slice(0, 5).map((r, i) => {
                                const t = r.trim().toUpperCase();
                                const isW = t === 'W';
                                const isL = t === 'L';
                                const isD = t === 'D';
                                return (
                                  <Chip
                                    key={"atas-"+i}
                                    label={t || '-'}
                                    size="small"
                                    sx={{
                                      height: 22,
                                      fontWeight: 700,
                                      bgcolor: isW ? 'success.light' : isL ? 'error.light' : isD ? 'warning.light' : 'default',
                                      color: isW || isL || isD ? '#fff' : 'text.secondary'
                                    }}
                                  />
                                );
                              })}
                            </Box>
                            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', justifyContent: 'flex-start' }}>
                              {lastTenArray.slice(5, 10).map((r, i) => {
                                const t = r.trim().toUpperCase();
                                const isW = t === 'W';
                                const isL = t === 'L';
                                const isD = t === 'D';
                                return (
                                  <Chip
                                    key={"bawah-"+i}
                                    label={t || '-'}
                                    size="small"
                                    sx={{
                                      height: 22,
                                      fontWeight: 700,
                                      bgcolor: isW ? 'success.light' : isL ? 'error.light' : isD ? 'warning.light' : 'default',
                                      color: isW || isL || isD ? '#fff' : 'text.secondary'
                                    }}
                                  />
                                );
                              })}
                            </Box>
                          </Box>
                        </Box>
                      </Paper>
                    );
                  })
                )}
              </Box>

              {/* Desktop/Table View (md and up) */}
              <TableContainer component={Paper} sx={{ display: { xs: 'none', md: 'block' }, mt: 3, maxWidth: 1000, mx: 'auto', borderRadius: 3, overflow: 'hidden',
                background: (theme) => theme.palette.mode === 'dark' ? 'linear-gradient(135deg, rgba(13,71,161,0.08) 0%, rgba(21,101,192,0.04) 100%)' : undefined,
                border: (theme) => theme.palette.mode === 'dark' ? '1px solid rgba(99,170,255,0.22)' : undefined
              }}>
                <Table sx={{ '& .MuiTableCell-root': { textAlign: 'center' } }}>
                  <TableHead sx={{
                    backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'transparent' : undefined,
                    '& .MuiTableCell-root': {
                      borderBottom: (theme) => theme.palette.mode === 'dark' ? '2px solid rgba(99,170,255,0.35)' : undefined,
                      fontWeight: 'bold'
                    }
                  }}>
                    <TableRow>
                      <TableCell>Rank</TableCell>
                      <TableCell>Nama Pemain</TableCell>
                      <TableCell>Poin</TableCell>
                      <TableCell>Jumlah Main</TableCell>
                      <TableCell sx={{ color: 'success.main' }}>Menang</TableCell>
                      <TableCell sx={{ color: 'error.main' }}>Kalah</TableCell>
                      <TableCell sx={{ color: 'warning.main' }}>Seri</TableCell>
                      <TableCell>10 Pertandingan Terakhir</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {ligaData.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                          <Typography variant="body2" color="text.secondary">
                            Belum ada data klasemen
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      getLigaComputedData().map((row, idx) => (
                        <TableRow key={`${row.nama}-${idx}`} hover>
                          <TableCell>{row.computedRank}</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>{row.nama}</TableCell>
                          <TableCell align="center">{row.poin}</TableCell>
                          <TableCell align="center">{row.jumlahMain}</TableCell>
                          <TableCell align="center">{row.menang}</TableCell>
                          <TableCell align="center">{row.kalah}</TableCell>
                          <TableCell align="center">{row.seri}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                              {(row.last10 || '').split(',').map((r, i) => {
                                const t = r.trim().toUpperCase();
                                const isW = t === 'W';
                                const isL = t === 'L';
                                const isD = t === 'D';
                                return (
                                  <Chip
                                    key={i}
                                    label={t || '-'}
                                    size="small"
                                    sx={{
                                      height: 22,
                                      fontWeight: 700,
                                      bgcolor: isW ? 'success.light' : isL ? 'error.light' : isD ? 'warning.light' : 'default',
                                      color: isW || isL || isD ? '#fff' : 'text.secondary'
                                    }}
                                  />
                                );
                              })}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Box>
      </TabPanel>
      )}

      {showOnlyRanked && (
        <Fab
          color="primary"
          size="medium"
          aria-label="Scroll ke atas"
          title="Scroll ke atas"
          onClick={scrollToTop}
          sx={{
            position: 'fixed',
            right: { xs: 16, sm: 24 },
            bottom: { xs: 16, sm: 24 },
            zIndex: 1100,
          }}
        >
          <ArrowUpwardIcon />
        </Fab>
      )}

      {/* Share Dialog */}
      <Dialog 
        open={shareDialogOpen} 
        onClose={handleCloseShareDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            backgroundColor: 'background.paper'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          pb: 1
        }}>
          <Typography variant="h6" component="div" sx={{ fontWeight: 700 }}>
            📤 Bagikan Leaderboard
          </Typography>
          <IconButton onClick={handleCloseShareDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Pilih platform untuk membagikan leaderboard {getCategoryLabel()}:
          </Typography>
          
          <List sx={{ pt: 0 }}>
            <ListItem 
              button 
              onClick={shareToWhatsApp}
              sx={{ 
                borderRadius: 2, 
                mb: 1,
                '&:hover': { backgroundColor: 'action.hover' }
              }}
            >
              <ListItemIcon>
                <WhatsAppIcon sx={{ color: '#25D366' }} />
              </ListItemIcon>
              <ListItemText 
                primary="WhatsApp" 
                secondary="Bagikan ke WhatsApp"
              />
            </ListItem>
            
            <ListItem 
              button 
              onClick={shareToTwitter}
              sx={{ 
                borderRadius: 2, 
                mb: 1,
                '&:hover': { backgroundColor: 'action.hover' }
              }}
            >
              <ListItemIcon>
                <TwitterIcon sx={{ color: '#1DA1F2' }} />
              </ListItemIcon>
              <ListItemText 
                primary="Twitter" 
                secondary="Tweet leaderboard"
              />
            </ListItem>
            
            <ListItem 
              button 
              onClick={shareToFacebook}
              sx={{ 
                borderRadius: 2, 
                mb: 1,
                '&:hover': { backgroundColor: 'action.hover' }
              }}
            >
              <ListItemIcon>
                <FacebookIcon sx={{ color: '#1877F2' }} />
              </ListItemIcon>
              <ListItemText 
                primary="Facebook" 
                secondary="Post ke Facebook"
              />
            </ListItem>
            
            <ListItem 
              button 
              onClick={shareToTelegram}
              sx={{ 
                borderRadius: 2, 
                mb: 1,
                '&:hover': { backgroundColor: 'action.hover' }
              }}
            >
              <ListItemIcon>
                <TelegramIcon sx={{ color: '#0088CC' }} />
              </ListItemIcon>
              <ListItemText 
                primary="Telegram" 
                secondary="Kirim ke Telegram"
              />
            </ListItem>
            
            <ListItem 
              button 
              onClick={copyToClipboard}
              sx={{ 
                borderRadius: 2, 
                mb: 1,
                '&:hover': { backgroundColor: 'action.hover' }
              }}
            >
              <ListItemIcon>
                <CopyIcon sx={{ color: 'primary.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary="Salin Link" 
                secondary="Copy ke clipboard"
              />
            </ListItem>
            
            <ListItem 
              button 
              onClick={downloadScreenshot}
              sx={{ 
                borderRadius: 2, 
                mb: 1,
                '&:hover': { backgroundColor: 'action.hover' }
              }}
            >
              <ListItemIcon>
                <DownloadIcon sx={{ color: 'success.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary="Download Screenshot" 
                secondary="Simpan sebagai gambar"
              />
            </ListItem>
          </List>
        </DialogContent>
      </Dialog>

      {/* Player Statistics Modal */}
      <PlayerStatsModal
        open={playerStatsModalOpen}
        onClose={handleClosePlayerStatsModal}
        player={selectedPlayer}
        category={selectedCategory}
      />

      {/* Reset Season Dialog */}
      <Dialog 
        open={resetSeasonDialogOpen} 
        onClose={() => {
          setResetSeasonDialogOpen(false);
          setResetPassword('');
          setSubmitError(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: 'warning.main', fontWeight: 700 }}>
          ⚠️ Konfirmasi Reset Season
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight={600}>
              Tindakan ini akan mereset poin semua pemain di ranked_double_solo:
            </Typography>
            <Typography variant="body2" component="div" sx={{ mt: 1 }}>
              • Poin ≥ 1000 → 300<br/>
              • Poin 251-999 → 250<br/>
              • Poin 0-250 → 200
            </Typography>
          </Alert>
          <TextField
            fullWidth
            type="password"
            label="Masukkan Password Konfirmasi"
            value={resetPassword}
            onChange={(e) => setResetPassword(e.target.value)}
            sx={{ mt: 2 }}
            error={!!submitError}
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setResetSeasonDialogOpen(false);
              setResetPassword('');
              setSubmitError(null);
            }}
            disabled={resetting}
          >
            Batal
          </Button>
          <Button 
            onClick={handleResetSeason}
            variant="contained"
            color="warning"
            disabled={resetting || !resetPassword}
            startIcon={resetting ? <CircularProgress size={16} /> : null}
          >
            {resetting ? 'Memproses...' : 'Reset Season'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payment error dialog for internal tournament payments */}
      <Dialog
        open={showPaymentErrorDialog}
        onClose={() => setShowPaymentErrorDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ textAlign: 'center', color: 'error.main' }}>
          ⚠️ Gagal Memproses Pembayaran
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Terjadi masalah saat mencoba memproses pembayaran. Silakan coba lagi atau hubungi admin jika masalah berlanjut.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPaymentErrorDialog(false)}>Tutup</Button>
          <Button variant="contained" onClick={handleRetryPayment}>
            Coba Lagi
          </Button>
        </DialogActions>
      </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={handleCloseSnackbar}
          message={snackbarMessage}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        />
      </Box>
    </Box>
  );
};

export default Leaderboard; 
